import React, { ChangeEvent, Fragment, useEffect, useState, useMemo, useRef } from "react";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faEllipsisV,
  faLongArrowRight,
  faPaperPlane,
  faRefresh,
  faTrashCanXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { useElements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe } from "@stripe/react-stripe-js";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import useBarcodeScanner from "hooks/barCodeScanner/barCodeScanner";

import { StatusCode } from "api/protocols";
import { GetRoomOccupants, IOccupant } from "api/rpc/room";
import { ICustomer } from "redux/reducers/models/customer";
import { ICart, ICartLineItem, IDiscountLines } from "redux/reducers/models/cart";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import {
  clearReaderDisplay,
  sheetInfo,
  processPayment as stripeProcessPayment,
  setReaderDisplay,
  convertOrderIntoDisplayInfo,
} from "helpers/StripeTerminalWrapper";
import {
  getDisplayCashChangeDue,
  isEmpty,
  printDesktopKitchenChit,
  printDesktopReceipt,
  displayCurrency,
  errorMessage,
} from "helpers/Helpers";
import { getOrderNetCollected, orderHasCollectedPayments } from "helpers/orderFinancial";
import { refundOrderPayments } from "helpers/refundOrderPayments";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";

import { IStripePaymentState } from "pages/secure/facility/teesheet/TeeSheet";
import PaymentMethods from "elements/register/cartMenu/PaymentMethods";
import CartTransactions from "elements/register/cartMenu/CartTransactions";

import Sheet from "components/sheet/Sheet";
import Popup from "components/popup/Popup";

import Divider from "components/divider";
import { ButtonNew as Button } from "components/buttonNew";
import CheckoutForm, { cancelManualCreditPaymentIntent } from "components/checkoutForm/CheckoutForm";
import Spin from "components/spin/spin";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import OrderCompletePopup from "components/orderCompletePopup/OrderCompletePopup";
import FormLayout from "components/form/FormLayout";

import "elements/teesheet/menu/teesheetMenu.scss";
import "elements/register/register.scss";
import GiftCardRedeem, { IGiftCardRedeemState } from "components/giftCardRedeem/GiftCardRedeem";
import GiftCardSell from "components/giftcardSell/GiftCardSell";
import AccountsModal from "components/accountsModal/AccountsModal";

import { useTranslation } from "react-i18next";
import LeagueSelectionModal from "components/leagueSelectionModal/LeagueSelectionModal";
import { IInvoice, IOrder } from "redux/reducers/models/order";
import { loadTableCart, postCart, postCartVariants, putCustomer } from "redux/actions/cart";
import { loadDiscountOptions } from "redux/actions/facility";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import CreditBookModal from "components/creditBookModal/CreditBookModal";
import { IPrizeAccount } from "pages/secure/facility/settings/prizeAccounts/PrizeAccount";
import { cloneDeep, isEqual } from "lodash";
import SelectCustomer from "elements/register/SelectCustomer";
import CartLineItemsNew from "pages/secure/facility/register/registerNew/cartLineItemsNew/CartLineItemsNew";
import { useRegisterContext } from "./RegisterContext";
import UpdateLineItemModalTemp, { IUpdateLineItemModalState } from "./UpdateLineItemModalTemp";
import { DeleteDiscountLine, PostDiscountLine } from "api/rpc/2024-04/facilityAdmin/order/cart/discountLine";
import ReactDOM from "react-dom";
import CartTotalsNew from "./cartTotalsNew/CartTotalsNew";
import { OrderProgress, StateType } from "../../teesheet/newTeeSheetDetails/NewTeeSheetDetails";
import Portal from "elements/Portal";
import { FolderMenu } from "components/folderMenu/FolderMenu";
import ProductsNew from "./ProductsNew";
import useLocalStorage from "hooks/useLocalStorage";
import { IRegisterGroup } from "../../Admin";

import {
  clearStoredProductFolders,
  getStoredProductFolder,
  isStoredProductFolderFresh,
  upsertStoredProductFolder,
} from "helpers/productFoldersStorage";
import { IProduct } from "redux/reducers/models/product";
import Icon from "components/icon/Icon";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { PostLineItemToCart, PutLineItemToCart } from "api/rpc/2024-04/facilityAdmin/order/cart/lineItem";
import TournamentSelectionModal from "components/tournamentSelectionModal/TournamentSelectionModal";
import { GetOrder, GetReceipt, PutOrderComplete, PutVoidOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { GetCustomer, GetCustomerPaymentMethod } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetGiftCardValidate } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";
import { PostKitchenChit } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/chit";
import { GetFolder, IFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import InvoiceModal from "components/invoiceModal/InvoiceModal";
import { PostMigrateCart } from "api/rpc/2024-04/facilityAdmin/order/invoice";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import {
  CloseTab,
  DeleteServiceFeeLineItem,
  PostCheckout,
  PostServiceFeeLineItem,
  PutCart,
  SaveCartAsTab,
  GetCart,
} from "api/rpc/2024-04/facilityAdmin/cart/cart";
import { NewBadgeGroup } from "components/badgeGroup/NewBadgeGroup";
import classNames from "classnames";
import { applyInventoryQuantitiesToProducts, fetchTrackedInventoryQuantities } from "helpers/registerInventory";
import { IChit } from "hooks/usePOSPrinter/usePOSPrinter";
import RedeemPointsModal from "./loyaltyPoints/RedeemPointsModal";
import useModal from "hooks/modals/useModal";
import { ILoyaltyCustomerExtended } from "../../settings/loyaltyProgram/loyaltyTabs/LoyaltyCustomers";
import { GetLoyaltyCustomer } from "api/rpc/2024-04/facilityAdmin/customer/loyaltyCustomer";
import { PostLoyaltyProgramDiscount } from "api/rpc/2024-04/facilityAdmin/discount/loyaltyProgramDiscount";
import RedeemTierPointsModal from "./loyaltyPoints/RedeemTierPointsModal";
import { IOffer } from "redux/reducers/models/loyalty";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import DiscountLineItemModal from "components/discountLineItemModal/DiscountLineItemModal";
import { IDiscount } from "api/rpc/facilityAdmin/facility/discount";
import { CustomIcon } from "components/customIcon/CustomIcon";
import {
  GetTicketStub,
  GetTicketValidate,
  PutTicketStubRedeemMultiple,
} from "api/rpc/2024-04/facilityAdmin/ticket/stub";
import Tabs from "components/tabs/Tabs";
import DataTable from "components/dataTable/DataTable";
import { Badge } from "components/badge/Badge";
import { IBookingDeposit } from "api/rpc/2024-04/facilityAdmin/deposit/deposit";
import BookingDepositsModal from "./bookingDepositsModal/BookingDepositsModal";
import TextField from "components/form/textField/TextField";
import ReviewPlayerDetailsModal from "../../teesheet/newTeeSheetDetails/reviewPlayerDetailsModal/ReviewPlayerDetailsModal";
import useFutureBookingPricingModal from "../../customer/futureBookingPricingModal/useFutureBookingPricingModal";
import { promptFutureBookingPricingAfterMembershipOrder } from "../../customer/futureBookingPricingModal/futureBookingPricingUtils";
import AttachGroupMembershipPopup from "./AttachGroupMembershipPopup";
import { TOrderMembershipSlot } from "api/rpc/2024-04/facilityAdmin/order/membershipSlots";
import TableServiceSendLineItemsModal from "pages/secure/facility/tableService/LineItemModals/TableServiceSendLineItemsModal";
import { ITable } from "api/rpc/facilityAdmin/tables/tables";
import Callout from "components/callout/Callout";
import {
  isAutoDiscountsDisabled,
  nextDisableAutoDiscountsValue,
  showDisableDiscountsOption,
} from "utils/cartDiscounts";
import CustomerDepositsModal from "./customerDepositsModal/CustomerDepositsModal";
import useOutstandingBalancePopup from "./outstandingBalancePopup/useOutstandingBalancePopup";
import { serializeGiftCardsForSale } from "helpers/giftCardExpiry";
import { TGiftCardParam } from "../../tableService/newTableService/tableServiceCart/TableServiceCart";
import CartAuditTrailDrawer from "./CartAuditTrailDrawer";
import { IStub } from "redux/reducers/models/ticket";
import {
  buildRedemptionVariantsFromRedeemedStubs,
  expandTicketStubIds,
  getMaxRedemptionQuantity,
  getTicketRedemptionQuantity,
  isMultiUseTicket,
  stubHasRedemptionProducts,
  TicketRedemptionQuantityMap,
} from "helpers/ticketRedemption";
import TicketRedemptionQuantityInput from "components/ticketRedemption/TicketRedemptionQuantityInput";
import "components/ticketRedemption/ticketRedemption.scss";
import CreditCardTab from "elements/teesheet/creditCard/CreditCardTab";
import { ICustomerPaymentMethod } from "api/rpc/2024-04/customer/paymentMethod";
import RedeemCashbackPointsModal from "./loyaltyPoints/RedeemCashbackPointsModal";

interface IMainState {
  page: "cart" | "methods" | "receipt";
  paymentOption: Record<string, any>;
  order: undefined | IOrder;
  serverPaymentSheetVisible: boolean;
  clearCartConfirmVisible: boolean;
  paymentAmount: number;
  customerSearchQuery: string;
  customerSearchResults: (Partial<ICustomer> | ICustomer)[];
  giftCardActive: boolean;
  sellGiftCardsActive: boolean;
  gift_cards: TGiftCardParam[];
  editCartLoading: boolean;
  accountsVisible: boolean;
  selectedAccount: Record<string, any>;
  creditBookVisible: boolean;
  selectedCreditBook: IPrizeAccount;
  selectedInvoice: IInvoice;
  searchingCustomer: boolean;
  changeDue: number;
  changePopUpVisible: boolean;
  roomChargeSheetVisible: boolean;
  roomOccupantSearchQuery: string;
  roomOccupantsSearchResults: IOccupant[];
  selectedOccupant: IOccupant;
  closeTableActive: boolean;
  orderCompletePopup: boolean;
  attachMembershipPopup: boolean;
  showCancelOrder: boolean;
  showRefundBeforeCancel: boolean;
  refundingPayments: boolean;
  leagueFeesActive: boolean;
  leagueFeeProducts: Array<ICartLineItem>;
  selectedLeagues: Array<number>;
  paymentInProgress: boolean;
  tournamentFeesActive: boolean;
  tournamentFeeProducts: Array<ICartLineItem>;
  selectedTournaments: Array<number>;
  void_note: string;
  invoiceVisible: boolean;
  is_tab: boolean;
  showCloseTab: boolean;
  closeTabReason: string;
  showAddNoteModal: boolean;
  addNote: string;
}

interface IRedeemTicketModalState {
  isOpen: boolean;
  selectedTab: number;
  ticketCode: string;
  availableTickets: any[];
  selectedCustomerTickets: any[];
  selectedCustomerTicketQuantities: TicketRedemptionQuantityMap;
  scannedTickets: any[];
  scannedTicketQuantities: TicketRedemptionQuantityMap;
  ticketOffset: number;
}

const TICKET_LIMIT = 50;

//Tee Sheet Details
type TStateOrigin = {
  stateCart: ICart;
  clearStateCart: () => void;
  updateState: <T>(newState: Partial<T>, type: StateType) => void;
  deposits?: Array<IBookingDeposit>;
  refreshDeposits?: (refresh: boolean) => void;
};

//Register
type TReduxOrigin = {
  setRegisterState?: (newMainState: Partial<any>) => void;
  setFilterState?: (newMainState: Partial<any>) => void;
  unPaidOrder?: IOrder;
  selectedFolder: IFolder;
  registerTabs?: ICart[];
};

type TDataOrigin = TReduxOrigin | TStateOrigin;

interface IProps {
  dataOrigin: TDataOrigin;
}
interface ICancelDiscountState {
  selectedDiscount: IDiscountLines;
  showCancelDiscount: boolean;
  parentLineItemId: number;
}

interface IAddItemState {
  modalVisible: boolean;
  folders: Array<IFolder>;
  selectedFolder: IFolder;

  products: Array<IProduct>;
  productSearch: string;
  productsLoadingMessage: string;
}

interface IPaymentMethodState {
  isOpen: boolean;
  selectedTabPaymentMethod: ICustomerPaymentMethod;
  customerPaymentMethods: ICustomerPaymentMethod[];
  useSavedMethod: boolean;
}

export default function CartMenuNew(props: IProps) {
  const elements = useElements();
  const stripe = useStripe();
  const { dataOrigin } = props;
  const { cartStore, authStore, facilityStore, terminalStore } = useAppSelector(store => store);
  const { getCurrencySymbol } = useCurrencyFacility();
  //Register
  const { setRegisterState, setFilterState, unPaidOrder, selectedFolder, registerTabs } = dataOrigin as TReduxOrigin;
  //Tee Sheet Details
  const { stateCart, updateState, clearStateCart, deposits, refreshDeposits } = dataOrigin as TStateOrigin;

  const [showDepositCallout, setShowDepositCallout] = useState<boolean>(true);

  const reduxCart = cartStore?.cart;
  const cart = isStateCart(dataOrigin) ? stateCart : reduxCart;
  // const clearCartRef = useRef<HTMLParagraphElement>(null);
  // const printReceiptRef = useRef<HTMLParagraphElement>(null);
  // const clearCartHovered = useHover(clearCartRef);
  // const printReceiptHovered = useHover(printReceiptRef);

  const registerGroup = useLocalStorage<IRegisterGroup>("register_group");
  const defaultFolderId = useLocalStorage<number>("default_folder_id");

  const reader = terminalStore?.reader;
  const permissions = authStore?.user?.permissions;

  const [reviewPlayersModalVisable, setReviewPlayersModalVisable] = useState(false);
  const { openIfNeeded: openFutureBookingPricingModalIfNeeded, modal: futureBookingPricingModal } =
    useFutureBookingPricingModal();
  const previousCartCustomerTypeIdRef = useRef<number | null>(null);

  const memoizedCustomer = useMemo(() => {
    return [cart?.customer];
  }, [cart?.customer]);

  const dispatch = useAppDispatch();
  const {
    registerState,
    updateRegisterState,
    loadingVariants,
    addLineItemUpdating,
    removeLineItemUpdating,
    lineItemsUpdating,
    addLoadingVariant,
    removeLoadingVariant,
  } = useRegisterContext();

  const { t } = useTranslation();
  const [viewCartAudit, setViewCartAudit] = useState<boolean>(false);
  const [submitTrigger, setSubmitTrigger] = useState<boolean>(false);
  const [manualProcessing, setManualProcessing] = useState<boolean>(false);
  const [lineItemState, setLineItemState] = useState<IUpdateLineItemModalState>({
    lineItem: null,
    inputQuantity: "",
    inputComboPrices: [],
    inputPrice: "",
    inputNote: "",
    isModalVisible: false,
    taxExemptionApplied: false,
  });
  const previousDynamicComboLineItemRef = useRef<ICartLineItem | null>(null);
  const previousModifierLineItemRef = useRef<ICartLineItem | null>(null);

  const [discountModal, setDiscountModal] = useState({
    isOpen: false,
    selectedDiscountId: -1,
    selectedDiscount: null,
    noteDiscount: "",
  });

  const [state, setState] = useState<IMainState>({
    page: "cart",
    paymentOption: null,
    order: undefined,
    clearCartConfirmVisible: false,
    paymentAmount: 0,
    customerSearchQuery: "",
    customerSearchResults: [],
    giftCardActive: false,
    sellGiftCardsActive: false,
    gift_cards: [],
    editCartLoading: false,
    accountsVisible: false,
    selectedAccount: null,
    creditBookVisible: false,
    selectedCreditBook: null,
    selectedInvoice: null,
    searchingCustomer: false,
    changeDue: 0,
    changePopUpVisible: false,
    roomChargeSheetVisible: false,
    roomOccupantSearchQuery: "",
    roomOccupantsSearchResults: [],
    selectedOccupant: null,
    closeTableActive: false,
    serverPaymentSheetVisible: false,
    orderCompletePopup: false,
    attachMembershipPopup: false,
    showCancelOrder: false,
    showRefundBeforeCancel: false,
    refundingPayments: false,
    leagueFeesActive: false,
    leagueFeeProducts: [],
    selectedLeagues: [],
    paymentInProgress: false,
    tournamentFeesActive: false,
    tournamentFeeProducts: [],
    selectedTournaments: [],
    void_note: "",
    invoiceVisible: false,
    is_tab: false,
    showCloseTab: false,
    closeTabReason: "",
    showAddNoteModal: false,
    addNote: "",
  });

  const [addItemState, setAddItemState] = useState<IAddItemState>({
    modalVisible: false,
    folders: [],
    selectedFolder: null,

    products: [],
    productSearch: "",
    productsLoadingMessage: "",
  });

  const [cancelDiscountState, setCancelDiscountState] = useState<ICancelDiscountState>({
    selectedDiscount: null,
    showCancelDiscount: false,
    parentLineItemId: 0,
  });

  const [paymentMethodState, setPaymentMethodState] = useState<IPaymentMethodState>({
    isOpen: false,
    customerPaymentMethods: [],
    selectedTabPaymentMethod: null,
    useSavedMethod: false,
  });

  const {
    state: clearDiscountsPopup,
    updateModal: updateClearDiscountsPopup,
    closeModal: closeClearDiscountsPopup,
  } = useModal();

  const {
    state: redeemPointsModal,
    updateModal: updateRedeemPointsModal,
    closeModal: closeRedeemPointsModal,
  } = useModal();

  // Tee sheet details deposit modal for checking players in
  const { state: depositsModal, updateModal: updateDepositsModal, closeModal: closeDepositsModal } = useModal();

  // Regular POS deposit modal
  const {
    state: customerDepositsModal,
    updateModal: updateCustomerDepositsModal,
    closeModal: closeCustomerDepositsModal,
  } = useModal();

  const { checkOutstandingBalance, outstandingBalancePopup } = useOutstandingBalancePopup();

  const {
    state: sendToKitchenModal,
    updateModal: openSendToKitchenModal,
    closeModal: closeSendToKitchenModal,
  } = useModal();

  const [loyaltyCustomer, setLoyaltyCustomer] = useState<
    ILoyaltyCustomerExtended & { points_available?: number; points_to_earn?: number }
  >(null);

  const handlePaymentOptionChange = (paymentOption: Record<string, any> | null) => {
    if (paymentOption === undefined) {
      return;
    }

    const selectingSameOption = paymentOption?.id != null && paymentOption.id === state.paymentOption?.id;

    setState(prevState => ({
      ...prevState,
      paymentOption: selectingSameOption ? null : paymentOption,
    }));

    if (cart?.payment_method_saved_on_tab) {
      setPaymentMethodState(prev => ({ ...prev, useSavedMethod: selectingSameOption }));
    }
  };

  function handleSelectSavedCard() {
    setState(prevState => ({ ...prevState, paymentOption: null }));
    setPaymentMethodState(prev => ({ ...prev, useSavedMethod: true }));
  }

  const handleBarcodeScan = async (scannedCode: string) => {
    if (state?.order || state?.page === "receipt") {
      return;
    }

    if (state.sellGiftCardsActive || state.giftCardActive) {
      return;
    }

    const isRegister = !isStateCart(dataOrigin);
    if (isRegister && !addItemState?.modalVisible) {
      return;
    }

    try {
      setAddItemState(prevState => ({
        ...prevState,
        productsLoadingMessage: t("secure.facility.register.register_new.register_new.005"),
      }));

      if (!isStateCart(dataOrigin)) {
        const { setRegisterState } = dataOrigin;
        setRegisterState?.({ productsLoadingMessage: t("secure.facility.register.register_new.register_new.005") });
      }

      const res = await GetVariant({ extended: true, barcode: scannedCode }, false);

      if (res.status === StatusCode.OK) {
        // Filter to only variants with facility access AND exact barcode match
        const variants = res.data.filter(
          variant =>
            variant.facility_access === true && (variant.barcode === scannedCode || variant?.sku === scannedCode),
        );

        if (variants.length >= 1) {
          const variant = variants[0];
          const product = variant?.product;
          void addLoadingVariant(variant, product);

          let updatedCartData: ICart | undefined;

          if (isStateCart(dataOrigin)) {
            // Tee Sheet Context
            const postVariantRes = await PostLineItemToCart(
              { cart_id: cart?.id, variants: [{ variant_id: variant?.id, quantity: 1, parent_id: 0 }] },
              false,
            );
            if (postVariantRes?.status === StatusCode.OK) {
              updatedCartData = postVariantRes.data;
              void updateState({ cart: updatedCartData }, StateType.CheckInState);
            }
          } else {
            // Register Context
            updatedCartData = await dispatch(
              postCartVariants(
                {
                  cart_id: cart?.id,
                  variants: [{ variant_id: variant?.id, quantity: 1, parent_id: 0 }],
                },
                false,
              ),
            );
          }

          setAddItemState(prevState => ({ ...prevState, productsLoadingMessage: "" }));
          if (!isStateCart(dataOrigin)) {
            const { setRegisterState } = dataOrigin;
            setRegisterState?.({ productsLoadingMessage: null });
          }

          void removeLoadingVariant(variant?.id);

          if (!updatedCartData) {
            dispatch(showError(t("secure.facility.register.register_new.register_new.006")));
          } else {
            dispatch(showSuccess(t("Product added to cart")));

            if (state.page === "methods") {
              ReactDOM.unstable_batchedUpdates(() => {
                setState(prevState => ({ ...prevState, page: "cart" }));
                updateRegisterState({ page: "cart" });
              });
            }

            if (product?.dynamic_price && updatedCartData?.line_items) {
              const addedLineItem = updatedCartData.line_items.find(
                (li: ICartLineItem) => li.variant_id === variant?.id && !li.parent_id,
              );
              if (addedLineItem) {
                updateRegisterState({ dynamicPriceLineItem: addedLineItem });
              }
            }
            // Clear search after successful scan
            setAddItemState(prevState => ({ ...prevState, productSearch: "" }));
          }
        } else {
          setAddItemState(prevState => ({ ...prevState, productsLoadingMessage: "" }));
          if (!isStateCart(dataOrigin)) {
            const { setRegisterState } = dataOrigin;
            setRegisterState?.({ productsLoadingMessage: "" });
          }
          dispatch(showError(t("No variant found with this barcode")));
        }
      } else {
        setAddItemState(prevState => ({ ...prevState, productsLoadingMessage: "" }));
        if (!isStateCart(dataOrigin)) {
          const { setRegisterState } = dataOrigin;
          setRegisterState?.({ productsLoadingMessage: "" });
        }
        dispatch(showError(t("secure.facility.register.register_new.register_new.008")));
      }
    } catch (error) {
      console.error("Error in handleBarcodeScan (CartMenuNew):", error);
      setAddItemState(prevState => ({ ...prevState, productsLoadingMessage: "" }));
      if (!isStateCart(dataOrigin)) {
        const { setRegisterState } = dataOrigin;
        setRegisterState?.({ productsLoadingMessage: "" });
      }
      dispatch(showError(t("Error scanning barcode")));
    }
  };

  useEffect(() => {
    updateRegisterState({
      barcodeScannerBlocked: state.sellGiftCardsActive || state.giftCardActive,
    });
  }, [state.sellGiftCardsActive, state.giftCardActive]);

  useEffect(() => {
    return () => {
      updateRegisterState({ barcodeScannerBlocked: false });
    };
  }, []);

  useEffect(() => {
    if (!facilityStore?.discountOptions?.length) {
      void dispatch(loadDiscountOptions());
    }
  }, [dispatch, facilityStore?.discountOptions?.length]);

  useBarcodeScanner(handleBarcodeScan, {
    interKeyDelay: 40,
    maxTotalDuration: 2000,
    minLength: 6,
    enabled: !state.sellGiftCardsActive && !state.giftCardActive,
  });

  // Stripe Payment Related Functions and Variables
  const [manualStripeState, setManualStripeState] = useState({
    clientSecret: "",
    visible: false,
    transaction: undefined,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
  });

  const [stripePaymentState, setStripePaymentState] = useState<IStripePaymentState>({
    onCancel: () => {},
    onOk: () => {},
    cancelText: "",
    okText: "",
    sheetVisible: false,
    titleText: "",
    message: "",
    convertedCode: "",
    processing: false,
    success: false,
  });

  const navigateBack = () => {
    if (state.page === "methods") {
      ReactDOM.unstable_batchedUpdates(() => {
        setShowDepositCallout(true);
        setState(prevState => ({ ...prevState, page: "cart", gift_cards: [], paymentMethod: "" }));
      });
    } else if (state.page === "cart") {
      //Close ?
    } else if (state.page === "receipt") {
      setState(prevState => ({ ...prevState, page: "cart", gift_cards: [], paymentMethod: "" }));
    }
  };

  useEffect(() => {
    //Return to cart page if a product is added when the payment methods page is displayed
    if (registerState.page) {
      ReactDOM.unstable_batchedUpdates(() => {
        setState(prevState => ({ ...prevState, page: registerState?.page }));
        updateRegisterState({ page: null });
      });
    }
  }, [registerState.page]);

  useEffect(() => {
    if (registerState.dynamicPriceLineItem) {
      handleLineItemClick(registerState.dynamicPriceLineItem);
      updateRegisterState({ dynamicPriceLineItem: null });
    }
  }, [registerState.dynamicPriceLineItem]);

  useEffect(() => {
    const previousDynamicComboLineItem = previousDynamicComboLineItemRef.current;
    previousDynamicComboLineItemRef.current = registerState.dynamicComboLineItem;

    if (previousDynamicComboLineItem && !registerState.dynamicComboLineItem && lineItemState.lineItem) {
      const updatedLineItem =
        cart?.line_items?.find(lineItem => lineItem.id === lineItemState.lineItem.id) ?? lineItemState.lineItem;

      setLineItemState(prevState => ({
        ...prevState,
        lineItem: updatedLineItem,
        isModalVisible: true,
      }));
    }
  }, [registerState.dynamicComboLineItem, cart?.line_items, lineItemState.lineItem]);

  useEffect(() => {
    const previousModifierLineItem = previousModifierLineItemRef.current;
    previousModifierLineItemRef.current = registerState.modifierLineItem;

    if (previousModifierLineItem && !registerState.modifierLineItem && lineItemState.lineItem) {
      const updatedLineItem =
        cart?.line_items?.find(lineItem => lineItem.id === lineItemState.lineItem.id) ?? lineItemState.lineItem;

      setLineItemState(prevState => ({
        ...prevState,
        lineItem: updatedLineItem,
        isModalVisible: true,
      }));
    }
  }, [registerState.modifierLineItem, cart?.line_items, lineItemState.lineItem]);

  useEffect(() => {
    if (unPaidOrder) {
      setState(prevState => ({ ...prevState, order: unPaidOrder, page: "methods" }));
    }
  }, [unPaidOrder]);

  //Display receipt on complete order
  useEffect(() => {
    if (state?.order) {
      // Reset for another
      if (state.order.balance > 0) {
        setState(prevState => ({ ...prevState, paymentAmount: Number(state?.order?.balance?.toFixed(2)) }));
      }
    }
  }, [state?.order]);

  //Update reader on cart change
  useEffect(() => {
    if (cart?.line_items && reader) {
      if (cart.total_price > 0) {
        void setReaderDisplay(convertOrderIntoDisplayInfo(cart, state.order));
      }
    }
  }, [cart, reader, cart?.tax_lines]);

  useEffect(() => {
    const loadPaymentMethods = async () => {
      const response = await loadCustomerPaymentMethods(cart?.customer_id);

      const paymentMethods = (response?.data ?? []) as ICustomerPaymentMethod[];
      const saved = cart?.payment_method_saved_on_tab;
      const methods =
        saved && !paymentMethods.some(method => method.id === saved.id) ? [saved, ...paymentMethods] : paymentMethods;

      setPaymentMethodState(prev => ({
        ...prev,
        customerPaymentMethods: methods,
      }));
    };

    if (paymentMethodState.isOpen) {
      void loadPaymentMethods();
    }
  }, [paymentMethodState.isOpen]);

  async function loadCustomerPaymentMethods(customer_id: number, paymentMethodId?: number): Promise<any> {
    const paymentMethodRes = await GetCustomerPaymentMethod({ customer_id: customer_id, id: paymentMethodId }, false);
    if (paymentMethodRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading customer payment methods")));
      return;
    }

    return paymentMethodRes;
  }

  //Set default paymentAmount
  useEffect(() => {
    if (cart) {
      setState(prevState => ({ ...prevState, paymentAmount: Number(cart.total_price?.toFixed(2)) }));

      if (loyaltyCustomer) {
        const totalBalance = loyaltyCustomer?.points_balance;
        const totalPointsUsed = cart?.discount_lines
          ?.filter(discount => discount?.loyalty_points_reward_id)
          ?.reduce((total, discount) => total + (discount?.loyalty_points_used ?? 0), 0);

        let totalPointsToEarn = 0;
        const loyaltyOffers = facilityStore?.loyaltyProgramOffers?.filter(offer => offer?.active);
        //Calculate points to earn
        if (totalPointsUsed <= 0 && loyaltyOffers?.length > 0) {
          loyaltyOffers?.forEach(offer => {
            if (offer?.type === "amount_spent") {
              const pointsEarned = processAmountSpentOffer(offer);
              totalPointsToEarn += pointsEarned;
            }
          });
          //Add items with loyalty points override
          cart?.line_items?.forEach(lineItem => {
            if (lineItem?.loyalty_points_override != null) {
              //Don't count discounts items if the loyalty program doesn't allow it
              if (facilityStore?.loyaltyProgram?.exclude_discounted_items) {
                const foundDiscount = cart?.discount_lines?.some(line => line?.cart_line_item_id === lineItem?.id);
                if (foundDiscount) {
                  return;
                }
              }
              totalPointsToEarn += lineItem?.loyalty_points_override * lineItem?.quantity;
            }
          });
        }
        setLoyaltyCustomer(prev => ({
          ...prev,
          points_available: totalBalance - totalPointsUsed,
          points_to_earn: totalPointsToEarn,
        }));
      }

      if (cart?.payment_method_saved_on_tab) {
        setPaymentMethodState(prev => ({
          ...prev,
          selectedTabPaymentMethod: cart?.payment_method_saved_on_tab,
          useSavedMethod: true,
        }));
      } else {
        setPaymentMethodState(prev => ({
          ...prev,
          selectedTabPaymentMethod: null,
          useSavedMethod: false,
        }));
      }
    }
  }, [cart, facilityStore?.loyaltyProgramOffers, facilityStore?.loyaltyProgram]);

  //Get loyalty customer information
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (cart?.customer) {
      void getLoyaltyCustomer(cart?.customer_id ?? 0, source.token);
    } else {
      setLoyaltyCustomer(null);
    }
    return () => source.cancel();
  }, [cart?.customer?.id]);

  //Return to cart page and reset gift cards when cart is switched
  useEffect(() => {
    if (cart && state.page !== "receipt") {
      setState(prevState => ({
        ...prevState,
        page: "cart",
        gift_cards: [],
        selectedLeagues: [],
        selectedTournaments: [],
      }));
    }
  }, [cart?.id]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (registerGroup && isStateCart(dataOrigin)) {
      void loadFolders(registerGroup.id, source.token);
    }
    return () => source.cancel();
  }, [registerGroup]);

  // Used for searching all products
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (() => {
          try {
            if (addItemState?.productSearch === "") {
              if (mounted) {
                setAddItemState(prevState => ({ ...prevState, products: [] }));
              }
              return;
            } else {
              if (mounted) {
                void loadProducts(source.token);
              }
            }
          } catch (error) {
            console.log("err", error);
          }
          return;
        })();
      }, 500);
    };
    if (isStateCart(dataOrigin)) {
      void search();
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      source.cancel();
    };
  }, [addItemState?.productSearch]);

  async function handleClearCart(clearWithInvoice?: boolean) {
    localStorage.removeItem("register_cart_token");
    localStorage.removeItem("register_cart_id");
    //Remove cart from cartStore
    void dispatch(loadTableCart({ tableCart: null }, false));
    await dispatch(postCart(null, false));
    if (!clearWithInvoice) {
      dispatch(showSuccess(t("Cart cleared successfully")));
    }
    ReactDOM.unstable_batchedUpdates(() => {
      setRegisterState({ order: undefined, unPaidOrder: undefined });
      setFilterState({ search: "" });
      setState(prevState => ({
        ...prevState,
        customerSearchQuery: "",
        customerSearchResults: [],
      }));
    });
    void clearReaderDisplay();
  }

  async function loadCustomers(customerSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: customerSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  async function searchForCustomers(mounted: boolean, customerSearchQuery: string, cancelToken: CancelToken) {
    try {
      if (customerSearchQuery === "") {
        if (mounted) {
          setState(prevState => ({ ...prevState, customerSearchResults: [null], searchingCustomer: false }));
        }
        return;
      } else {
        setState(prevState => ({ ...prevState, searchingCustomer: true }));
        const res = await loadCustomers(customerSearchQuery, cancelToken);
        if (mounted) {
          setState(prevState => ({ ...prevState, customerSearchResults: res, searchingCustomer: false }));
        }
      }
    } catch (error) {
      console.error(error);
      setState(prevState => ({ ...prevState }));
    }
    return;
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void searchForCustomers(mounted, state.customerSearchQuery, source.token);
      }, 300);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, customerSearchResults: [null], searchingCustomer: false }));
      source.cancel("Cancelled");
    };
  }, [state.customerSearchQuery]);

  async function loadRoomOccupants(roomOccupantSearchQuery: string, cancelToken: CancelToken) {
    const roomOccupantsRes = await GetRoomOccupants({ search: roomOccupantSearchQuery }, true, cancelToken);

    if (roomOccupantsRes.status !== StatusCode.OK && roomOccupantsRes.message !== "Cancelled") {
      dispatch(showError(roomOccupantsRes.message));
      return;
    }

    return roomOccupantsRes;
  }

  async function searchForRoomOccupants(mounted: boolean, roomOccupantSearchQuery: string, cancelToken: CancelToken) {
    try {
      if (roomOccupantSearchQuery === "") {
        if (mounted) {
          setState(prevState => ({ ...prevState, roomOccupantsSearchResults: [] }));
        }
      } else {
        const res = await loadRoomOccupants(roomOccupantSearchQuery, cancelToken);
        if (mounted && res?.status === StatusCode.OK && res?.data) {
          const occupants = res.data.map(occupant => {
            return {
              reservation_number: occupant.reservation_number ?? "",
              room_number: occupant.room_number ?? "",
              res_status: occupant.res_status,
              guest_name: occupant.guest_name ?? "",
              credit_limit: occupant.credit_limit,
            };
          });
          setState(prevState => ({ ...prevState, roomOccupantsSearchResults: occupants }));
        }
      }
    } catch (error) {
      console.error(error);
      setState(prevState => ({ ...prevState }));
    }
    setState(prevState => ({
      ...prevState,
      selectedOccupant: null,
    }));
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void searchForRoomOccupants(mounted, state.roomOccupantSearchQuery, source.token);
      }, 2000);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, roomOccupantsSearchResults: [] }));
      source.cancel("Cancelled");
    };
  }, [state.roomOccupantSearchQuery]);

  async function completeOrder(order_id: number, showLoader = true) {
    const register = JSON.parse(localStorage.getItem("register"));
    const registerId = register?.id;

    const completeRes = await PutOrderComplete(
      {
        order_id,
        gift_cards: serializeGiftCardsForSale(state.gift_cards),
        register_id: registerId,
        league_ids: state.selectedLeagues,
        tournament_ids: state.selectedTournaments,
      },
      showLoader,
    );

    if (completeRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.086")));
      return;
    }
    dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.087")));
    ReactDOM.unstable_batchedUpdates(() => {
      if (!isStateCart(dataOrigin)) {
        setFilterState({ search: "" });
      }
      setState(prevState => ({
        ...prevState,
        customerSearchQuery: "",
        customerSearchResults: [],
        gift_cards: null,
        selectedLeagues: null,
      }));
    });

    return completeRes?.data as IOrder;
  }

  async function loadReceipt(order_id: number) {
    const receiptRes = await GetReceipt({ id: order_id }, true);

    if (receiptRes.status === StatusCode.OK) {
      window.open().document.write(receiptRes.data as string);
    } else {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.088")));
    }
  }

  async function clearCartAsync(order: any) {
    const previousCartCustomerTypeId = cart?.customer?.cart_customer_type_id ?? null;
    previousCartCustomerTypeIdRef.current = previousCartCustomerTypeId;
    const completedOrder = await completeOrder(order.id as number, !isStateCart(dataOrigin));

    // clear the cart info from local storage
    localStorage.removeItem("register_cart_token");
    localStorage.removeItem("register_cart_id");

    if (reader) {
      void clearReaderDisplay();
    }

    ReactDOM.unstable_batchedUpdates(() => {
      if (isStateCart(dataOrigin)) {
        updateState(
          {
            order: {
              ...order,
              loyalty_program_transactions: completedOrder?.loyalty_program_transactions
                ? completedOrder?.loyalty_program_transactions
                : [],
            },
          },
          StateType.CheckInState,
        );
      }
      setState(prevState => ({
        ...prevState,
        order: {
          ...order,
          membership_slots: completedOrder?.membership_slots ?? order?.membership_slots,
          loyalty_program_transactions: completedOrder?.loyalty_program_transactions
            ? completedOrder?.loyalty_program_transactions
            : [],
        },
        paymentAmount: 0,
        page: "receipt",
        selectedAccount: undefined,
        paymentOption: null,
      }));
      void closeDepositsModal();
    });

    const register = JSON.parse(localStorage.getItem("register"));

    //check register to send kitchen chit
    if (register?.send_kitchen) {
      await sendKitchenChits();
    }

    //Check register to auto log out
    if (register && register?.auto_log_out === true) {
      localStorage.setItem("switch_user_after_transaction", "true");
    }

    //Refresh inventory data
    if (!isStateCart(dataOrigin) && selectedFolder) {
      //Register folders
      void loadInventoryLevels(selectedFolder, order as IOrder);
    } else if (isStateCart(dataOrigin) && addItemState?.selectedFolder) {
      //Tee Sheet Details folders
      void loadInventoryLevels(addItemState?.selectedFolder, order as IOrder);
    }

    //Order completed, display order action bar
    ReactDOM.unstable_batchedUpdates(() => {
      if (!isStateCart(dataOrigin)) {
        setRegisterState({ unPaidOrder: null });
      }
      const completedUnassignedSlots = ((completedOrder?.membership_slots ?? []) as TOrderMembershipSlot[]).filter(
        slot => !slot.customer_id && !slot.customer_membership_id,
      );
      setState(prevState => ({
        ...prevState,
        attachMembershipPopup: completedUnassignedSlots.length > 0,
        orderCompletePopup: !isStateCart(dataOrigin) && completedUnassignedSlots.length === 0,
      }));
    });

    const unassignedSlots = ((completedOrder?.membership_slots ?? []) as TOrderMembershipSlot[]).filter(
      slot => !slot.customer_id && !slot.customer_membership_id,
    );

    if (completedOrder && unassignedSlots.length === 0) {
      await promptFutureBookingPricingAfterMembershipOrder(
        {
          customer_id: completedOrder.customer_id ?? order.customer_id,
          line_items: order.line_items,
          membership_slots: completedOrder.membership_slots,
          previousCartCustomerTypeId,
        },
        openFutureBookingPricingModalIfNeeded,
      );
    }
  }

  async function updateOrder(order_id: number, clearCart: boolean) {
    const displayLoader = !isStateCart(dataOrigin);
    if (order_id) {
      //Update order
      const orderRes = await GetOrder({ id: order_id, extended: true }, displayLoader);
      if (orderRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.cart_menu.089")));
        return;
      }
      setManualProcessing(false);

      const order = orderRes.data[0];
      if (order.balance <= 0 || order.financial_status === "paid") {
        if (clearCart) {
          await clearCartAsync(order);
        }
      } else {
        // Still need to process another payment for this order
        ReactDOM.unstable_batchedUpdates(() => {
          setState(prevState => ({
            ...prevState,
            order: order,
            paymentAmount: order.balance.toFixed(2),
            page: "methods",
          }));
          if (isStateCart(dataOrigin)) {
            updateState({ order: order }, StateType.CheckInState);
            updateState(
              {
                isOpen: false,
                clearOnClose: true,
                loading: false,
                transactionComplete: false,
              },
              StateType.OrderComplete,
            );
          }
        });
      }
    }
  }

  async function sendKitchenChits() {
    const lineItemIds = getDefaultKitchenLineItemIds();

    if (lineItemIds?.length > 0) {
      const chitRes = await PostKitchenChit({ line_item_ids: lineItemIds }, false);

      if (chitRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.cart_menu.003")));
        return;
      }

      const chits = chitRes?.data;
      let chitCount = 0;
      const tasks = [];

      for (const chit of chits) {
        chit.chit_number = ++chitCount;
        chit.total_chits = chits.length;

        tasks.push(printDesktopKitchenChit(chit as IChit, t));
      }

      Promise.all(tasks)
        .then(results => console.log(results))
        .catch(error => dispatch(showError(error as string)));
    }
  }

  function getDefaultKitchenLineItemIds(): number[] {
    return (
      cart?.line_items
        ?.filter(
          item =>
            !item.parent_id &&
            item.kitchen_status !== "sent" &&
            (item.product?.meta?.kitchen_location_id ||
              item.product?.meta?.kitchen_location_id_2 ||
              item.product?.meta?.kitchen_location_id_3),
        )
        ?.map(item => item.id) ?? []
    );
  }

  function handleSendToKitchen() {
    void openSendToKitchenModal({ isOpen: true });
  }

  async function handleSendChit(lineItemIds: number[]) {
    const res = await PostKitchenChit({ line_item_ids: lineItemIds }, false);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending chits")));
      return;
    }

    const chits = res?.data;
    let chitCount = 0;
    const tasks: Promise<void>[] = [];

    for (const chit of chits) {
      chit.chit_number = ++chitCount;
      chit.total_chits = chits.length;
      tasks.push(printDesktopKitchenChit(chit as IChit, t));
    }

    Promise.all(tasks)
      .then(() => dispatch(showSuccess(t("Sent to kitchen"))))
      .catch(error => dispatch(showError(error as string)));

    const cartRes = await GetCart({ id: cart?.id }, false);
    if (cartRes.status === StatusCode.OK && cartRes?.data?.length > 0) {
      void dispatch(loadTableCart({ tableCart: cartRes.data[0] }, false));
    }
    void closeSendToKitchenModal();
  }

  function changePaymentAmount(e: any) {
    const value = e.target.value;

    setState(prevState => ({ ...prevState, paymentAmount: value }));
  }

  function handleManualProcessing() {
    if (manualProcessing === true) {
      setManualProcessing(false);
    } else {
      setManualProcessing(true);
    }
  }

  const elementsComplete =
    manualStripeState.elementComplete.cardNumber === true &&
    manualStripeState.elementComplete.cardExpiry === true &&
    manualStripeState.elementComplete.cardCvc === true;

  const {
    StripeTerminalSheet,
    StripeTerminalRefundSheet,
    processPayment,
    processRefund,
    paymentStatus,
    order: tempStripePaymentOrder,
  } = useStripeTerminalPayment();

  const handleProcessPayment = async (
    giftCardInfo?: IGiftCardRedeemState,
    deposit?: IBookingDeposit & { amountToApply?: string },
  ) => {
    //Show loader on register
    if (!isStateCart(dataOrigin)) {
      dispatch(enqueue());
    }

    // If using an invoice as a payment method don't create order
    if (state.paymentOption?.payment_method === "invoice") {
      if (!state.selectedInvoice) {
        dispatch(dequeue());
        dispatch(showError("No invoice selected"));
        return;
      }

      const migrateRes = await PostMigrateCart({ cart_id: cart.id, invoice_id: state.selectedInvoice.id }, true);

      if (migrateRes.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(showError("Error migrating cart to invoice"));
        return;
      }

      if (isStateCart(dataOrigin)) {
        void clearStateCart();
      } else {
        await handleClearCart();
      }
      dispatch(dequeue());
      dispatch(showSuccess("Cart migrated to invoice successfully"));
      return;
    }

    setState(prevState => ({ ...prevState, paymentInProgress: true }));
    const cartTotalPrice = Number(cart.total_price?.toFixed(2));
    try {
      const cart_id = cart.id;

      // Checking out produces a new order

      let checkoutRes: { status: any; data: any };

      // Create a new order if order does not exist in state
      if (!state?.order) {
        const register = JSON.parse(localStorage.getItem("register"));
        const registerId = register?.id;

        //Disable add fee buttons while the order is being created
        if (isStateCart(dataOrigin)) {
          updateState(
            {
              orderStep: OrderProgress.CREATING_CART,
            },
            StateType.OrderProgress,
          );
        }

        checkoutRes = await PostCheckout({ cart_id, register_id: registerId }, false);
      }

      const finalAmount = Number(state.paymentAmount);

      //Check to see if amount is acceptable
      if (Number.isNaN(finalAmount)) {
        void dequeueLoader();
        dispatch(showError(`'${state.paymentAmount} ${t("secure.facility.register.register_new.cart_menu.004")} `));
        setState(prevState => ({ ...prevState, paymentInProgress: false }));
        return;
      }

      if (
        (finalAmount > cartTotalPrice && state.paymentOption?.payment_method !== "cash") ||
        (finalAmount < 0 && state.paymentOption?.payment_method !== "account")
      ) {
        void dequeueLoader();
        dispatch(showError(`'${state.paymentAmount} ${t("secure.facility.register.register_new.cart_menu.005")} `));
        setState(prevState => ({ ...prevState, paymentInProgress: false }));
        return;
      }

      let showOrderCompletePopup = false;
      if (
        isStateCart(dataOrigin) &&
        ((!state?.order && finalAmount >= checkoutRes?.data?.order?.total_price) ||
          (state?.order && finalAmount >= state?.order?.balance) ||
          (state?.order && !state?.order?.balance && finalAmount >= state?.order?.total_price))
      ) {
        showOrderCompletePopup = true;
      }

      if (checkoutRes?.status === StatusCode.OK || state?.order) {
        if (checkoutRes) {
          ReactDOM.unstable_batchedUpdates(() => {
            setState(prevState => ({ ...prevState, order: checkoutRes.data.order }));
            if (!isStateCart(dataOrigin)) {
              setRegisterState({ order: checkoutRes.data.order });
            }
            if (isStateCart(dataOrigin)) {
              ReactDOM.unstable_batchedUpdates(() => {
                updateState(
                  {
                    orderStep: OrderProgress.ORDER_IN_PROGRESS,
                  },
                  StateType.OrderProgress,
                );
                updateState({ order: checkoutRes?.data?.order }, StateType.CheckInState);
              });
            }
          });
        }

        const order_id = checkoutRes ? checkoutRes.data.order.id : state?.order.id;

        if (state.paymentOption?.payment_method === "manual_card") {
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: finalAmount,
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
            },
            false,
          );

          if (transactionRes.status === StatusCode.OK) {
            setManualStripeState(prevState => ({
              ...prevState,
              transaction: transactionRes.data,
              clientSecret: transactionRes.data.client_secret,
              visible: true,
            }));
          } else {
            void dequeueLoader();
            dispatch(showError(t("secure.facility.register.register_new.cart_menu.006")));
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            if (isStateCart(dataOrigin)) {
              updateState(
                {
                  isOpen: false,
                  clearOnClose: true,
                  loading: false,
                  transactionComplete: false,
                },
                StateType.OrderComplete,
              );
            }
            return;
          }
          // Using terminal - display modal
        } else if (state.paymentOption?.payment_method === "card") {
          void dequeueLoader();

          if (facilityStore?.facility.terminal_integration === "server") {
            setState(prevState => ({ ...prevState, serverPaymentSheetVisible: true }));
            // If cart already has a tip, skip tipping
            await processPayment(
              order_id as number,
              state.paymentOption,
              finalAmount,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              cart?.total_tip > 0 ? true : false,
            );
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
          } else {
            setStripePaymentState(prevState => ({
              ...prevState,
              sheetVisible: true,
              onCancel: undefined,
              okText: t("secure.facility.register.register_new.cart_menu.007"),
              processing: true,
              onOk: async () => {
                setStripePaymentState(prevState => ({
                  ...prevState,
                  sheetVisible: false,
                  onCancel: undefined,
                  okText: undefined,
                  processing: false,
                  onOk: () => {},
                  titleText: "",
                  success: false,
                  message: "",
                }));
                await clearReaderDisplay();
              },
              titleText: t("secure.facility.register.register_new.cart_menu.008"),
              message: t("secure.facility.register.register_new.cart_menu.009"),
            }));

            await stripeProcessPayment(
              order_id as number,
              finalAmount,
              state.paymentOption,
              handleStripeSuccess,
              handleStripeError,
            );
          }
          if (!isStateCart(dataOrigin)) {
            dispatch(enqueue());
          }
        } else if (state.paymentOption?.payment_method === "gift_card") {
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: finalAmount,
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
              gift_card_code: giftCardInfo.giftCardCode,
              gift_card_pin: giftCardInfo.giftCardPin,
            },
            false,
          );

          if (transactionRes.status === StatusCode.OK) {
            setState(prevState => ({
              ...prevState,
              giftCardActive: false,
            }));

            // display success notification
            dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.010")));

            if (isStateCart(dataOrigin)) {
              // If gift card balnce wasn't enough to cover transaction don't show order complete popup
              if (transactionRes.data?.amount < finalAmount) {
                showOrderCompletePopup = false;
              }
              if (showOrderCompletePopup) {
                updateState({ loading: true, isOpen: true }, StateType.OrderComplete);
              }
            }

            await updateOrder(transactionRes.data.order_id, true);
            //Handle successful payment
          } else {
            dispatch(
              showError(
                transactionRes?.status === StatusCode.BAD_REQUEST && typeof transactionRes?.data === "string"
                  ? transactionRes?.data
                  : t("secure.facility.register.register_new.cart_menu.006"),
              ),
            );
            void dequeueLoader();
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            if (isStateCart(dataOrigin)) {
              updateState(
                {
                  isOpen: false,
                  clearOnClose: true,
                  loading: false,
                  transactionComplete: false,
                },
                StateType.OrderComplete,
              );
            }
            return;
          }
        } else if (state.paymentOption?.payment_method === "account") {
          // display success notification
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: finalAmount,
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
              account_id: state.selectedAccount.id,
            },
            false,
          );

          if (transactionRes?.status !== StatusCode.OK) {
            dispatch(showError(t("secure.facility.register.register_new.cart_menu.006")));
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            void dequeueLoader();
            return;
          }

          // display success notification
          dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.010")));

          if (showOrderCompletePopup && isStateCart(dataOrigin)) {
            updateState({ loading: true, isOpen: true }, StateType.OrderComplete);
          }

          // Update order and check if there is a remaining balance
          await updateOrder(transactionRes.data.order_id, true);
        } else if (state.paymentOption?.payment_method === "credit_book") {
          // display success notification
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: finalAmount,
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
              credit_book_id: state.selectedCreditBook.id,
            },
            false,
          );

          if (transactionRes?.status !== StatusCode.OK) {
            dispatch(showError(t("secure.facility.register.register_new.cart_menu.006")));
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            void dequeueLoader();
            if (isStateCart(dataOrigin)) {
              updateState(
                {
                  isOpen: false,
                  clearOnClose: true,
                  loading: false,
                  transactionComplete: false,
                },
                StateType.OrderComplete,
              );
            }
            return;
          }

          // display success notification
          dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.010")));

          if (showOrderCompletePopup && isStateCart(dataOrigin)) {
            updateState({ loading: true, isOpen: true }, StateType.OrderComplete);
          }

          // Update order and check if there is a remaining balance
          await updateOrder(transactionRes.data.order_id, true);
        } else if (
          state.paymentOption?.payment_method === "room_charge_rdp" ||
          state.paymentOption?.payment_method === "maestro"
        ) {
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: finalAmount,
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
              guest_name: state.selectedOccupant?.guest_name,
              room_number:
                state.paymentOption?.payment_method === "room_charge_rdp"
                  ? state.selectedOccupant?.room_number
                  : state.roomOccupantSearchQuery,
            },
            false,
          );

          if (transactionRes.status !== StatusCode.OK) {
            dispatch(showError(t("secure.facility.register.register_new.cart_menu.006")));
            void dequeueLoader();
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            if (isStateCart(dataOrigin)) {
              updateState(
                {
                  isOpen: false,
                  clearOnClose: true,
                  loading: false,
                  transactionComplete: false,
                },
                StateType.OrderComplete,
              );
            }
            return;
          } else {
            dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.010")));

            if (showOrderCompletePopup && isStateCart(dataOrigin)) {
              updateState({ loading: true, isOpen: true }, StateType.OrderComplete);
            }

            // Update order and check if there is a remaining balance
            await updateOrder(transactionRes.data.order_id, true);

            closeRoomChargeSheet();
          }
        } else if (state.paymentOption?.payment_method === "deposit") {
          // display success notification
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: Math.min(Number(deposit?.amountToApply), finalAmount),
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
              deposit_id: deposit?.id,
            },
            false,
          );

          if (transactionRes?.status !== StatusCode.OK) {
            dispatch(
              showError(
                t(typeof transactionRes?.data === "string" ? transactionRes?.data : "Error processing transaction"),
              ),
            );
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            void dequeueLoader();
            if (isStateCart(dataOrigin)) {
              updateState(
                {
                  isOpen: false,
                  clearOnClose: true,
                  loading: false,
                  transactionComplete: false,
                },
                StateType.OrderComplete,
              );
            }
            return;
          }

          // display success notification
          dispatch(showSuccess(t("Transaction generated successfully")));

          if (showOrderCompletePopup && isStateCart(dataOrigin) && Number(deposit?.amountToApply) >= finalAmount) {
            updateState({ loading: true, isOpen: true }, StateType.OrderComplete);
          }

          if (refreshDeposits) {
            void refreshDeposits(true);
          }

          // Update order and check if there is a remaining balance
          await updateOrder(transactionRes.data.order_id, true);
        } else {
          let exactChangeDue: number;
          const roundCashChange = facilityStore.teeSheetSettings?.register_cash_round_change === true;

          if (showOrderCompletePopup && isStateCart(dataOrigin)) {
            updateState({ loading: true, isOpen: true }, StateType.OrderComplete);
          }

          //Display change due for cash payments
          if (state.paymentOption?.payment_method === "cash") {
            if (state?.order?.balance && Number(state.paymentAmount) > state.order?.balance) {
              exactChangeDue = Number(state.paymentAmount) - state.order?.balance;
              setState(prevState => ({
                ...prevState,
                changeDue: getDisplayCashChangeDue(exactChangeDue, roundCashChange),
                changePopUpVisible: true,
              }));
            } else if (Number(state.paymentAmount) > cartTotalPrice) {
              exactChangeDue = Number(state.paymentAmount) - cartTotalPrice;
              setState(prevState => ({
                ...prevState,
                changeDue: getDisplayCashChangeDue(exactChangeDue, roundCashChange),
                changePopUpVisible: true,
              }));
            }
          }
          const transactionRes = await PostTransaction(
            {
              kind: "sale",
              amount: exactChangeDue ? finalAmount - exactChangeDue : finalAmount,
              order_id: order_id,
              payment_option_id: state.paymentOption?.id,
            },
            false,
          );

          if (transactionRes?.status !== StatusCode.OK) {
            dispatch(showError(t("secure.facility.register.register_new.cart_menu.006")));
            void dequeueLoader();
            setState(prevState => ({ ...prevState, paymentInProgress: false }));
            if (isStateCart(dataOrigin)) {
              updateState(
                {
                  isOpen: false,
                  clearOnClose: true,
                  loading: false,
                  transactionComplete: false,
                },
                StateType.OrderComplete,
              );
            }
            return;
          }

          // display success notification
          dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.010")));

          // Update order and check if there is a remaining balance
          await updateOrder(transactionRes.data.order_id, true);

          //Handle successful payment
        }

        if (showOrderCompletePopup && isStateCart(dataOrigin)) {
          updateState({ transactionComplete: true }, StateType.OrderComplete);
        }
      }
    } catch (e) {
      if (isStateCart(dataOrigin)) {
        updateState(
          {
            isOpen: false,
            clearOnClose: true,
            loading: false,
            transactionComplete: false,
          },
          StateType.OrderComplete,
        );
      }
      console.log("err:", e);
    }
    setState(prevState => ({ ...prevState, paymentInProgress: false }));
    void dequeueLoader();
  };

  async function handleSaveChangedTabCard() {
    const method = paymentMethodState.selectedTabPaymentMethod;
    if (!cart?.id || !method?.id) {
      setPaymentMethodState(prev => ({ ...prev, isOpen: false }));
      return;
    }

    const res = await PutCart({ id: cart.id, customer_payment_method_id: method.id }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating payment method on tab")));
      return;
    }

    dispatch({
      type: "cart.update",
      payload: {
        isLoaded: true,
        cart: res.data,
      },
    });

    setState(prev => ({ ...prev, paymentOption: null }));
    setPaymentMethodState(prev => ({
      ...prev,
      isOpen: false,
      useSavedMethod: true,
      selectedTabPaymentMethod: res.data?.payment_method_saved_on_tab ?? method,
    }));
  }

  async function handleQuickCharge() {
    const savedMethod = cart?.payment_method_saved_on_tab;
    const paymentOption = facilityStore?.paymentOptions?.find(option => option?.payment_method === savedMethod?.type);

    if (!savedMethod || !paymentOption) {
      dispatch(showError(t("Please select a payment method")));
      return;
    }

    const enteredAmount = Number(state.paymentAmount);
    const chargeAmount =
      Number.isFinite(enteredAmount) && enteredAmount > 0 ? enteredAmount : Number(cart.total_price_due?.toFixed(2));

    try {
      dispatch(enqueue());
      const register = JSON.parse(localStorage.getItem("register"));
      const registerId = register?.id;

      const checkoutRes = await PostCheckout(
        {
          cart_id: cart?.id,
          register_id: registerId,
        },
        false,
      );

      if (checkoutRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error creating order")));
        return;
      }

      const order = checkoutRes.data.order;
      const orderId = order.id;

      const transactionRes = await PostTransaction(
        {
          kind: "sale",
          amount: chargeAmount,
          order_id: orderId,
          payment_option_id: paymentOption.id,
        },
        false,
      );

      if (transactionRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error processing transaction")));
        return;
      }

      const transaction = transactionRes.data;

      const clientSecret = transaction?.client_secret;
      const transactionId = transaction?.id;

      if (!clientSecret || !transactionId) {
        dispatch(showError(t("Unable to initialize card payment")));
        return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: savedMethod.stripe_payment_method_id,
      });

      if (stripeError) {
        dispatch(
          showError(
            stripeError.decline_code === "insufficient_funds"
              ? t("Card declined due to insufficient funds.")
              : stripeError.message,
          ),
        );
        return;
      }

      const captureRes = await PutCapture(
        {
          payment_intent_id: paymentIntent.id,
          transaction_id: transactionId,
        },
        false,
      );

      if (captureRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Payment cannot be processed as it failed to capture the card.")));
        return;
      }

      dispatch(showSuccess(t("Transaction generated successfully")));

      void updateOrder(Number(orderId), true);
      setState(prevState => ({ ...prevState, page: "receipt" }));
    } catch {
      dispatch(showError(t("Error processing transaction")));
    } finally {
      dispatch(dequeue());
    }
  }

  function dequeueLoader() {
    if (!isStateCart(dataOrigin)) {
      dispatch(dequeue());
    }
  }

  const handleStripeError = (message: string, sheetInfo: sheetInfo) => {
    //set sheet
    setStripePaymentState(prevState => ({
      ...prevState,
      onCancel: () => {
        sheetInfo.onCancel();
        setStripePaymentState(prevState => ({ ...prevState, sheetVisible: false }));
      },
      onOk: () => {
        sheetInfo.onOk();
        setStripePaymentState(prevState => ({
          ...prevState,
          sheetVisible: true,
          onCancel: undefined,
          okText: t("secure.facility.register.register_new.cart_menu.007"),
          processing: true,
          onOk: async () => {
            setStripePaymentState(prevState => ({
              ...prevState,
              sheetVisible: false,
              onCancel: undefined,
              okText: undefined,
              processing: false,
              onOk: () => {},
              titleText: "",
              success: false,
              message: "",
            }));
            await clearReaderDisplay();
          },
          titleText: t("secure.facility.register.register_new.cart_menu.008"),
          message: t("secure.facility.register.register_new.cart_menu.009"),
        }));
      },
      cancelText: sheetInfo.cancelText,
      okText: sheetInfo.okText,
      sheetVisible: true,
      titleText: sheetInfo.title,
      message: sheetInfo.message,
      convertedCode: sheetInfo.convertedErrorCode,
      success: false,
      processing: false,
    }));
  };

  const handleStripeSuccess = (message?: string, order_id?: number) => {
    setStripePaymentState(prevState => ({
      ...prevState,
      sheetVisible: true,
      onCancel: undefined,
      okText: t("secure.facility.register.register_new.cart_menu.011"),
      //resets state
      onOk: () => {
        setStripePaymentState(prevState => ({
          ...prevState,
          sheetVisible: false,
          onCancel: undefined,
          okText: undefined,
          processing: false,
          onOk: () => {},
          titleText: "",
          message: "",
        }));
      },
      processing: false,
      success: true,
      titleText: t("secure.facility.register.register_new.cart_menu.012"),
      message: t("secure.facility.register.register_new.cart_menu.013"),
    }));
    void updateOrder(order_id, true);
    setState(prevState => ({ ...prevState, page: "receipt" }));
    dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.013")));
  };

  const handleLineItemClick = (lineItem: ICartLineItem) => {
    setLineItemState({
      ...lineItemState,
      isModalVisible: true,
      lineItem: lineItem,
      inputQuantity: String(lineItem.quantity),
      inputPrice: String(lineItem.price),
      inputNote: lineItem.note ? lineItem.note : "",
      taxExemptionApplied: lineItem.tax_exemption_applied,
    });
  };

  async function removeAllDiscountLines() {
    const discountLines = cart?.discount_lines;
    if (discountLines?.length > 0) {
      //Add loader for all line items with discount lines
      cart?.line_items?.forEach(lineItem => {
        const foundDiscountLine = discountLines?.some(discount => discount?.cart_line_item_id === lineItem?.id);
        if (!lineItem?.parent_id && foundDiscountLine) {
          void addLineItemUpdating(lineItem?.id);
        }
      });

      void closeClearDiscountsPopup();

      const discountLineIds = discountLines?.map(discount => discount?.id);
      const discountsRes = await Promise.all(
        discountLineIds?.map(discountId => DeleteDiscountLine({ id: discountId, cart_id: cart?.id }, false)),
      );

      if (discountsRes?.some(res => res?.status !== StatusCode.OK)) {
        dispatch(showError(t("Error removing discount")));
      }

      const cartRes = await GetCart({ id: cart?.id }, false);
      if (cartRes?.status !== StatusCode.OK || cartRes?.data?.length === 0) {
        dispatch(showError(t("Error getting updated cart")));
        return;
      }

      const updatedCart = cartRes?.data[0];

      //Remove loader for all line items with discount liness
      cart?.line_items?.forEach(lineItem => {
        const foundDiscountLine = discountLines?.some(discount => discount?.cart_line_item_id === lineItem?.id);
        if (!lineItem?.parent_id && foundDiscountLine) {
          void removeLineItemUpdating(lineItem?.id);
        }
      });

      //State cart (Tee Sheet Details)
      if (isStateCart(dataOrigin)) {
        updateState({ cart: updatedCart }, StateType.CheckInState);
      } else {
        //Redux cart (Register)
        void dispatch(loadTableCart({ tableCart: updatedCart }, false));
      }

      dispatch(showSuccess(t("Discounts cleared successfully")));
    }
  }

  const removeDiscountLine = async () => {
    const selectedDiscount = { ...cancelDiscountState.selectedDiscount };
    //If modifier discount is being removed, show loader for the entire parent item + modifiers
    const selectedParentLineItemId = cancelDiscountState?.parentLineItemId;

    void addLineItemUpdating(selectedParentLineItemId ?? selectedDiscount?.cart_line_item_id);
    setCancelDiscountState(prevState => ({
      ...prevState,
      showCancelDiscount: false,
      selectedDiscount: null,
    }));
    const res = await DeleteDiscountLine({ id: selectedDiscount?.id, cart_id: selectedDiscount?.cart_id }, false);
    void removeLineItemUpdating(selectedParentLineItemId ?? selectedDiscount?.cart_line_item_id);

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.018")));
      return;
    }

    if (isStateCart(dataOrigin)) {
      void updateState({ cart: res?.data }, StateType.CheckInState);
    } else {
      void dispatch(loadTableCart({ tableCart: res?.data }, false));
    }

    dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.092")));
  };

  const handleCustomerSearch = (query: string) => {
    setState(prevState => ({ ...prevState, customerSearchQuery: query }));
  };

  const handleCustomerSelection = async (id: string | number) => {
    if (cart?.id) {
      if (cart.is_tab) {
        setState(prevState => ({ ...prevState, editCartLoading: true }));

        const params = {
          cart_id: cart?.id,
          customer_id: id,
          description: cart?.description,
        };

        const tabRes = await SaveCartAsTab(params, true);

        if (tabRes.status !== StatusCode.OK) {
          setState(prevState => ({ ...prevState, editCartLoading: false }));
          return;
        }

        dispatch({
          type: "cart.update",
          payload: {
            isLoaded: true,
            cart: tabRes.data,
          },
        });

        localStorage.setItem("register_cart_id", String(cart.id));
        setState(prevState => ({ ...prevState, editCartLoading: false }));

        ReactDOM.unstable_batchedUpdates(() => {
          setFilterState({ reloadRegisterTabs: true });
        });

        void checkOutstandingBalance(id);
      } else {
        setState(prevState => ({ ...prevState, editCartLoading: true }));
        if (isStateCart(dataOrigin)) {
          const res = await PutCart({ id: cart?.id, customer_id: id }, false);
          if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("secure.facility.register.register_new.cart_menu.098")));
          } else {
            updateState({ cart: res?.data }, StateType.CheckInState);
            void checkOutstandingBalance(id);
          }
        } else {
          const updatedCart = await dispatch(putCustomer(cart.id, parseInt(id.toString()), false));

          //Display review players modal
          if (
            updatedCart &&
            !isStateCart(dataOrigin) &&
            facilityStore?.facility?.display_review_players_modal &&
            updatedCart?.customer_id &&
            (!facilityStore?.teeSheetSettings?.review_players_modal_customer_types ||
              facilityStore?.teeSheetSettings?.review_players_modal_customer_types?.length === 0 ||
              facilityStore?.teeSheetSettings?.review_players_modal_customer_types?.includes(
                updatedCart?.customer?.customer_type_id,
              ))
          ) {
            setReviewPlayersModalVisable(true);
          }

          if (updatedCart) {
            void checkOutstandingBalance(id);
          }
        }
        setState(prevState => ({ ...prevState, editCartLoading: false }));
      }
    }
  };

  const removeCustomerFromCart = async () => {
    if (cart?.id) {
      setState(prevState => ({ ...prevState, editCartLoading: true }));
      if (isStateCart(dataOrigin)) {
        const res = await PutCart({ id: cart?.id, customer_id: null }, false);
        if (res?.status !== StatusCode.OK) {
          dispatch(showError(t("secure.facility.register.register_new.cart_menu.099")));
        } else {
          updateState({ cart: res?.data }, StateType.CheckInState);
        }
      } else {
        await dispatch(putCustomer(cart?.id, null, false));
        ReactDOM.unstable_batchedUpdates(() => {
          setFilterState({ reloadRegisterTabs: true });
        });
      }
      setState(prevState => ({ ...prevState, editCartLoading: false }));
      handleCustomerSearch("");
    }
  };

  const initiateAddGiftCard = () => {
    setState(prevState => ({ ...prevState, giftCardActive: true }));
  };
  const handleCancelAddGiftCard = () => {
    setState(prevState => ({ ...prevState, giftCardActive: false, gift_card_code: "", gift_card_pin: "" }));
  };

  // Check for specific products like gift cards or green fees, or if a customer is required
  const checkProducts = () => {
    const gift_cards: any[] = [];
    const filteredGiftCards: any[] = cart.line_items?.filter(
      (line_item: Record<string, any>) => line_item?.product?.type === "Gift Card",
    );
    const filteredLeagueFees = cart.line_items?.filter(
      (line_item: Record<string, any>) => line_item?.product?.type === "League Registration Fee",
    );
    const customerRequiredFees = cart.line_items?.filter(
      (line_item: Record<string, any>) => line_item?.product?.customer_required as boolean,
    );

    if (filteredLeagueFees?.length > 0 && !cart?.customer_id) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.078")));
      return;
    }

    // Check if cart contains a product that has the customer_required flag
    if (customerRequiredFees?.length > 0 && !cart?.customer_id) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.079")));
      return;
    }

    const filteredTournamentFees = cart?.line_items?.filter(
      (line_item: Record<string, any>) => line_item?.product?.type === "Tournament Registration Fee",
    );

    if (filteredTournamentFees?.length > 0 && !cart?.customer_id) {
      dispatch(showError(t("Add customer to the cart to purchase tournament registration fee")));
      return;
    }

    //Open gift cards modal if cart contains gift cards
    if (filteredGiftCards?.length > 0 || filteredLeagueFees?.length > 0 || filteredTournamentFees?.length > 0) {
      //Check for gift cards
      if (filteredGiftCards.length > 0) {
        filteredGiftCards.forEach((line_item: Record<string, any>) => {
          for (let i = 0; i < line_item?.quantity; i++) {
            gift_cards.push({
              product_id: line_item.product_id,
              cart_line_item_id: line_item.id,
              code: null,
              pin: null,
              random: true,
              balance: line_item.price,
              reload: 0,
              gift_card_restrict_facility: line_item.product?.gift_card_restrict_facility,
              gift_card_expiry_type: line_item.product?.gift_card_expiry_type,
              gift_card_expiry_years: line_item.product?.gift_card_expiry_years,
            });
          }
        });
        setState(prevState => ({ ...prevState, gift_cards, sellGiftCardsActive: true }));
      }
      //Check for league registration fees
      if (filteredLeagueFees?.length > 0) {
        setState(prevState => ({ ...prevState, leagueFeesActive: true, leagueFeeProducts: filteredLeagueFees }));
      }

      //Check for tournament registration fees
      if (filteredTournamentFees?.length > 0) {
        setState(prevState => ({
          ...prevState,
          tournamentFeesActive: true,
          tournamentFeeProducts: filteredTournamentFees,
        }));
      }
    } else {
      //Go directly to payment methods page if no gift cards / league fees in cart
      setState(prevState => ({
        ...prevState,
        page: "methods",
        gift_cards: null,
        leagueFeeProducts: null,
        selectedLeagues: null,
      }));
    }
  };

  const setSelectedLeagues = (leagues: Array<number>) => {
    const moveToPaymentMethods = checkToContinuePayment("leagues");
    setState(prevState => ({
      ...prevState,
      selectedLeagues: leagues,
      page: moveToPaymentMethods ? "methods" : "cart",
      leagueFeesActive: false,
      leagueFeeProducts: null,
    }));
  };

  const setSelectedTournaments = (tournaments: Array<number>) => {
    const moveToPaymentMethods = checkToContinuePayment("tournaments");
    setState(prevState => ({
      ...prevState,
      selectedTournaments: tournaments,
      page: moveToPaymentMethods ? "methods" : "cart",
      tournamentFeesActive: false,
      tournamentFeeProducts: null,
    }));
  };

  function cancelGiftCardsAndLeagues() {
    setState(prevState => ({
      ...prevState,
      page: "cart",
      sellGiftCardsActive: false,
      gift_cards: null,
      leagueFeesActive: false,
      leagueFeeProducts: null,
      selectedLeagues: null,
      selectedTournaments: null,
      tournamentFeesActive: false,
      tournamentFeeProducts: null,
    }));
  }

  const validateGiftCardCode = async (code: string, reload: number) => {
    const validateRes = await GetGiftCardValidate({ code, reload }, true);
    if (validateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.019")));
      return false;
    }
    return true;
  };

  const handleAddSellGiftCards = async () => {
    let validateRes = true;
    //Validate gift card codes
    for (const gift_card of state.gift_cards) {
      if (!gift_card.random) {
        validateRes = await validateGiftCardCode(gift_card.code, gift_card.reload);
      }
      if (validateRes === false) {
        return;
      }
    }

    if (validateRes) {
      const moveToPaymentMethods = checkToContinuePayment("gift_card");
      setState(prevState => ({
        ...prevState,
        page: moveToPaymentMethods ? "methods" : "cart",
        sellGiftCardsActive: false,
      }));
      dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.020")));
    }
  };

  const checkToContinuePayment = (type: "gift_card" | "leagues" | "tournaments") => {
    switch (type) {
      case "gift_card":
        if (
          !state.selectedLeagues &&
          state.leagueFeesActive &&
          state.tournamentFeesActive &&
          !state.selectedTournaments
        ) {
          return false;
        }
        return true;
      case "leagues":
        if (state.sellGiftCardsActive && state.tournamentFeesActive && !state.selectedTournaments) {
          return false;
        }
        return true;
      case "tournaments":
        if (state.sellGiftCardsActive && !state.selectedLeagues && state.leagueFeesActive) {
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  useEffect(() => {
    if (state.selectedAccount || state.selectedCreditBook || state.selectedInvoice) {
      void handleProcessPayment();
    }
    return () => {
      setState(prevState => ({
        ...prevState,
        selectedAccount: undefined,
        selectedCreditBook: undefined,
        selectedInvoice: undefined,
      }));
    };
  }, [state.selectedAccount, state.selectedCreditBook, state.selectedInvoice]);

  useEffect(() => {
    if (paymentStatus === "completed" && tempStripePaymentOrder === null) {
      void clearCartAsync(state.order);
      setState(prevState => ({ ...prevState, serverPaymentSheetVisible: false }));
    }

    if ((paymentStatus === "partial_payment" || paymentStatus === "paid") && tempStripePaymentOrder !== null) {
      //Show order complete modal on tee sheet details
      if (paymentStatus === "paid" && isStateCart(dataOrigin)) {
        updateState(
          {
            isOpen: true,
            loading: true,
          },
          StateType.OrderComplete,
        );
      }
      setState(prevState => ({
        ...prevState,
        order: tempStripePaymentOrder,
        paymentAmount: tempStripePaymentOrder.balance.toFixed(2),
        page: "methods",
      }));
      setState(prevState => ({ ...prevState, serverPaymentSheetVisible: false }));
    }
  }, [paymentStatus, tempStripePaymentOrder, state.order]);

  function closeAccountsModal() {
    setState(prevState => ({
      ...prevState,
      accountsVisible: false,
      selectedAccount: undefined,
    }));
  }
  function closeCreditBookModal() {
    setState(prev => ({ ...prev, creditBookVisible: false, selectedCreditBook: undefined }));
  }

  function closeInvoiceModal() {
    setState(prev => ({ ...prev, invoiceVisible: false, selectedInvoice: undefined }));
  }

  function handleAccountsOk(selectedAccount: Record<string, any>) {
    setState(prevState => ({ ...prevState, accountsVisible: false, selectedAccount }));
  }

  function handleCreditBooksOk(selectedCreditBook: IPrizeAccount) {
    setState(prevState => ({ ...prevState, creditBookVisible: false, selectedCreditBook }));
  }

  function handleInvoiceOk(selectedInvoice: IInvoice) {
    setState(prevState => ({ ...prevState, invoiceVisible: false, selectedInvoice }));
  }

  const defaultRoomChargeSheetState: Partial<IMainState> = {
    roomOccupantSearchQuery: "",
    roomOccupantsSearchResults: [],
    selectedOccupant: null,
  };

  function initiateRoomChargeSheet() {
    setState(prevState => ({
      ...prevState,
      roomChargeSheetVisible: true,
      ...defaultRoomChargeSheetState,
    }));
  }

  function closeRoomChargeSheet() {
    setState(prevState => ({
      ...prevState,
      roomChargeSheetVisible: false,
      ...defaultRoomChargeSheetState,
    }));
  }

  function handleOccupantSearchQueryChange(event: any) {
    setState(prevState => ({ ...prevState, roomOccupantSearchQuery: event.target.value }));
  }

  function handleProcessPaymentCheck() {
    if (cart?.payment_method_saved_on_tab && state?.paymentOption === null) {
      void handleQuickCharge();
    } else if (state.paymentOption?.payment_method === "gift_card") {
      void initiateAddGiftCard();
    } else if (state.paymentOption?.payment_method === "account") {
      setState(prevState => ({ ...prevState, accountsVisible: true }));
    } else if (state.paymentOption?.payment_method === "credit_book") {
      setState(prevState => ({ ...prevState, creditBookVisible: true }));
    } else if (state.paymentOption?.payment_method === "invoice") {
      setState(prevState => ({ ...prevState, invoiceVisible: true }));
    } else if (
      state.paymentOption?.payment_method === "room_charge_rdp" ||
      state.paymentOption?.payment_method === "maestro"
    ) {
      initiateRoomChargeSheet();
    } else if (state?.paymentOption?.payment_method === "deposit" && deposits && deposits?.length > 0) {
      // Deposit for checking player in on tee sheet details page (lists player deposits within the tee time)
      void updateDepositsModal({ isOpen: true });
    } else if (state?.paymentOption?.payment_method === "deposit") {
      // For applying deposit to order on regular POS (lists the deposits of the customer on the cart)
      void updateCustomerDepositsModal({ isOpen: true });
    } else {
      void handleProcessPayment();
    }
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setManualStripeState(prevState => ({
      ...prevState,
      elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  async function handlePrintReceiptButton() {
    const receiptRes = await GetReceipt({ id: cart?.id, type: "cart" }, true);
    if (receiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.088")));
      return;
    }

    printDesktopReceipt(facilityStore.facility?.id, receiptRes.data.toString() as string);
  }

  async function closeOrderCompletePopup(open: boolean) {
    if (isStateCart(dataOrigin)) {
      //Reset state cart
      ReactDOM.unstable_batchedUpdates(() => {
        updateState({ cart: null, order: null }, StateType.CheckInState);
        updateState({ orderStep: OrderProgress.NEW_ORDER }, StateType.OrderProgress);
        updateState(
          {
            isOpen: false,
            clearOnClose: true,
            loading: false,
            transactionComplete: false,
          },
          StateType.OrderComplete,
        );
      });
    } else {
      // Create another empty redux cart
      dispatch(enqueue());
      await dispatch(postCart(null, false));
      dispatch(dequeue());
      setRegisterState({ order: undefined, unPaidOrder: undefined });
    }
    setState(prevState => ({ ...prevState, orderCompletePopup: open, page: "cart", order: undefined }));
    setFilterState({ reloadRegisterTabs: true });
  }

  function handleCancelOrderClick() {
    const order = state?.order;
    if (orderHasCollectedPayments(order?.financial_status, order?.transactions, order?.balance, order?.total_price)) {
      setState(prevState => ({ ...prevState, showRefundBeforeCancel: true }));
      return;
    }
    setState(prevState => ({ ...prevState, showCancelOrder: true }));
  }

  async function handleRefundBeforeCancel() {
    const order = state?.order;
    if (!order?.id || state.refundingPayments) {
      return;
    }

    setState(prevState => ({ ...prevState, refundingPayments: true }));
    const refundResult = await refundOrderPayments({
      orderId: order.id,
      transactions: order.transactions,
      processInteracRefund: processRefund,
      hasReader: Boolean(reader),
      note: "Refunded payments before cancel",
    });

    if (!refundResult.ok) {
      setState(prevState => ({ ...prevState, refundingPayments: false }));
      dispatch(showError(refundResult.message || t("Failed to refund payments")));
      return;
    }

    dispatch(showSuccess(t("Payments refunded")));
    setState(prevState => ({
      ...prevState,
      showRefundBeforeCancel: false,
      refundingPayments: false,
    }));
    await cancelOrder();
  }

  function handleContinueCancelWithoutRefund() {
    setState(prevState => ({
      ...prevState,
      showRefundBeforeCancel: false,
      showCancelOrder: true,
    }));
  }

  async function cancelOrder() {
    const isUnpaid = state?.order?.financial_status === "unpaid";
    const cancelRes = await PutVoidOrder(
      {
        order_id: state?.order?.id,
        notes: "Transaction cancelled",
        financial_status: isUnpaid ? "cancelled" : "voided",
        void_note: state?.void_note,
      },
      true,
    );

    if (cancelRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.register.register_new.cart_menu.093")));
      return;
    }
    dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.095")));
    void navigateBack();
    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({
        ...prevState,
        order: undefined,
        showCancelOrder: false,
        showRefundBeforeCancel: false,
        paymentAmount: cart ? Number(cart.total_price.toFixed(2)) : prevState?.paymentAmount,
        void_note: "",
      }));
      if (!isStateCart(dataOrigin)) {
        setRegisterState({ order: undefined, unPaidOrder: undefined });
      }
      if (isStateCart(dataOrigin)) {
        updateState({ order: undefined }, StateType.CheckInState);
        updateState(
          {
            isOpen: false,
            clearOnClose: true,
            loading: false,
            transactionComplete: false,
          },
          StateType.OrderComplete,
        );
      }
    });
  }

  async function loadFolders(registerGroupId: number, token?: CancelToken) {
    const folderRes = await GetFolder({ register_group_id: registerGroupId, extended: false }, false, token);

    if (token && token.reason) {
      return;
    }
    if (folderRes.status !== StatusCode.OK) {
      if (token && token?.reason) {
        return;
      }
      setAddItemState(prevState => ({ ...prevState, folders: [] }));
      dispatch(showError(t("secure.facility.register.register.008")));
      return;
    }

    const folders = folderRes.data;

    // Get default folder for the selected Register or the default set for the facility
    let selectedFolder: IFolder = undefined;

    if (defaultFolderId > 0) {
      selectedFolder = folders.find(folder => folder.id === defaultFolderId);
    } else {
      selectedFolder = folders.find(folder => folder.default === true);
    }

    if (!selectedFolder) {
      selectedFolder = folders?.[0];
    }

    setAddItemState(prevState => ({ ...prevState, folders: folders }));

    if (selectedFolder) {
      void handleChangeSelectedFolder(selectedFolder);
    }
  }

  async function handleChangeSelectedFolder(selectedFolder: IFolder) {
    if (selectedFolder?.id === undefined) {
      return;
    }

    const storedProductFolder = await getStoredProductFolder(selectedFolder.id);

    if (isStoredProductFolderFresh(selectedFolder, storedProductFolder)) {
      setAddItemState(prevState => ({ ...prevState, selectedFolder: storedProductFolder }));
    } else {
      const folderRes = await GetFolder({ id: selectedFolder.id, extended: true }, true);

      if (folderRes.status !== StatusCode.OK || folderRes.data?.length === undefined || folderRes.data.length === 0) {
        dispatch(showError(t("secure.facility.register.register.011")));
        return;
      }

      const newSelectedFolder = folderRes.data[0];
      await upsertStoredProductFolder(newSelectedFolder);

      setAddItemState(prevState => ({ ...prevState, selectedFolder: newSelectedFolder }));
    }
  }

  function renderProducts() {
    if (addItemState?.productSearch && addItemState?.products) {
      const products = addItemState?.products?.sort((firstProd, secondProd) => {
        if (firstProd.title < secondProd.title) {
          return -1;
        } else if (firstProd.title > secondProd.title) {
          return 1;
        } else {
          return 0;
        }
      }) as [];
      return { useGridPositions: false, products };
    } else {
      const products = addItemState?.selectedFolder?.products ?? ([] as []);
      return { useGridPositions: true, products };
    }
  }

  async function refreshFolderStorage() {
    await clearStoredProductFolders();

    if (addItemState?.selectedFolder) {
      void handleChangeSelectedFolder(addItemState?.selectedFolder);
    }
    dispatch(showSuccess(t("secure.facility.register.register.012")));
  }

  async function loadProducts(cancelToken: CancelToken) {
    setAddItemState(prevState => ({
      ...prevState,
      productsLoadingMessage: t("secure.facility.register.register_new.cart_menu.102"),
    }));
    //Search products
    const productRes = await GetProduct({ extended: true, search: addItemState?.productSearch }, false, cancelToken);
    setAddItemState(prevState => ({ ...prevState, productsLoadingMessage: null }));
    if (productRes.status === StatusCode.OK) {
      setAddItemState(prevState => ({ ...prevState, products: productRes?.data ?? [] }));
    } else {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("secure.facility.register.register.100")));
    }
  }

  async function handleAddTip(totalTip: number, tipAllocations?: Array<{ user_id: number; amount: number }>) {
    const cartRes = await PutCart(
      {
        id: cart?.id,
        total_tip: totalTip,
        ...(permissions?.orders_edit_tip_allocations
          ? { tip_allocations: tipAllocations ?? (totalTip > 0 ? null : []) }
          : {}),
      },
      true,
    );

    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.register.register.107")));
      return;
    }

    void updateState({ cart: cartRes?.data }, StateType.CheckInState);
  }

  async function handleAddServiceFee(serviceFeeId: number) {
    const params = {
      service_fee_id: serviceFeeId,
      facility_id: facilityStore?.facility?.id,
      cart_id: cart?.id,
    };

    const cartRes = await PostServiceFeeLineItem(params, true);

    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding service fee")));
      return;
    }

    //State cart (Tee Sheet Details)
    if (isStateCart(dataOrigin)) {
      updateState({ cart: cartRes?.data }, StateType.CheckInState);
    } else {
      //Redux cart (Register)
      void dispatch(loadTableCart({ tableCart: cartRes?.data }, false));
    }
  }

  function handleOpenCreateTab(cart: ICart) {
    ReactDOM.unstable_batchedUpdates(() => {
      setFilterState({ openNewTab: true, currentCart: cart, newTabCustomer: cart.customer });
    });
  }

  const cartLoading = loadingVariants?.current?.length > 0 || lineItemsUpdating?.current?.length > 0;

  async function handleSaveTab() {
    setFilterState({ reloadRegisterTabs: true });
    await dispatch(postCart(null, true));
  }

  function handleCartUpdate(cart: ICart) {
    if (!isStateCart(dataOrigin)) {
      setFilterState({ updatedTab: cart });
    }
  }

  function handleShowCloseTab() {
    setState(prevState => ({ ...prevState, showCloseTab: true }));
  }

  // function handleCloseTabReasonChange(event: any) {
  //   setState(prevState => ({ ...prevState, closeTabReason: event.target.value }));
  // }

  async function handleCloseTab() {
    const params = {
      cart_id: cart?.id,
      cancel_reason: state?.closeTabReason,
    };

    const closeTabRes = await CloseTab(params, true);

    if (closeTabRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error closing tab")));
      return;
    }

    if (isStateCart(dataOrigin)) {
      void clearStateCart();
    } else {
      void handleClearCart();
    }
    setState(prevState => ({
      ...prevState,
      showCloseTab: false,
      closeTabReason: "",
    }));

    setFilterState({ reloadRegisterTabs: true });
  }

  async function loadInventoryLevels(folder: IFolder, cartOrder: IOrder) {
    let productIds: Array<number> = [];
    let variantIds: Array<number> = [];
    const order = { ...cartOrder };
    const selectedFolder = { ...folder };
    //Update inventory levels of purchased items
    order?.line_items?.forEach(lineItem => {
      const foundProduct = selectedFolder?.products?.find(product => product?.id === lineItem?.product_id);
      //Only take variant ids of product that has track_inventory set to true
      if (foundProduct && foundProduct?.track_inventory) {
        if (!productIds?.includes(lineItem?.product_id)) {
          productIds = [...productIds, lineItem?.product_id];
        }
        variantIds = [...variantIds, lineItem?.variant_id];
      }
    });
    if (variantIds?.length > 0) {
      const { status, getQuantity } = await fetchTrackedInventoryQuantities(variantIds, false);
      if (status !== StatusCode.OK) {
        dispatch(showError(t("Error getting inventory details")));
        return;
      }
      const storedProductFolder = await getStoredProductFolder(selectedFolder?.id);
      if (storedProductFolder && productIds?.length > 0) {
        const updatedProducts = applyInventoryQuantitiesToProducts(
          storedProductFolder.products ?? [],
          productIds,
          getQuantity,
        );
        const updatedFolder = { ...storedProductFolder, products: updatedProducts };
        await upsertStoredProductFolder(updatedFolder);
        if (!isStateCart(dataOrigin) && setRegisterState) {
          //Register
          setRegisterState({ selectedFolder: updatedFolder });
        } else {
          //Tee Sheet Details
          setAddItemState(prev => ({ ...prev, selectedFolder: updatedFolder }));
        }
      }
    }
  }

  async function getLoyaltyCustomer(customerId: number, cancelToken: CancelToken) {
    const res = await GetLoyaltyCustomer(
      {
        customer_id: customerId,
        offset: 0,
        tier: facilityStore?.loyaltyProgram?.type === "tier_based" ? true : undefined,
      },
      false,
      cancelToken,
    );
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting customer loyalty information")));
      return;
    }
    if (res?.data && res?.data?.length >= 1) {
      const loyaltyProgramCustomer = res?.data[0];
      const totalBalance = loyaltyProgramCustomer?.points_balance;
      const totalPointsUsed = cart?.discount_lines
        ?.filter(discount => discount?.loyalty_points_reward_id)
        ?.reduce((total, discount) => total + discount?.loyalty_points_used, 0);
      let totalPointsToEarn = 0;
      const loyaltyOffers = facilityStore?.loyaltyProgramOffers?.filter(offer => offer?.active);
      //Calculate points to earn
      if (totalPointsUsed <= 0 && loyaltyOffers?.length > 0) {
        loyaltyOffers?.forEach(offer => {
          if (offer?.type === "amount_spent") {
            const pointsEarned = processAmountSpentOffer(offer);
            totalPointsToEarn += pointsEarned;
          }
        });

        //Add items with loyalty points override
        cart?.line_items?.forEach(lineItem => {
          if (lineItem?.loyalty_points_override != null) {
            //Don't count discounts items if the loyalty program doesn't allow it
            if (facilityStore?.loyaltyProgram?.exclude_discounted_items) {
              const foundDiscount = cart?.discount_lines?.some(line => line?.cart_line_item_id === lineItem?.id);
              if (foundDiscount) {
                return;
              }
            }
            totalPointsToEarn += lineItem?.loyalty_points_override * lineItem?.quantity;
          }
        });
      }
      const extendedCustomer = {
        ...loyaltyProgramCustomer,
        customer_first_name: cart?.customer?.first_name,
        customer_last_name: cart?.customer?.last_name,
        customer_email: cart?.customer?.email ?? "",
        customer_type: (cart?.customer as any)?.customer_type?.title ?? (cart?.customer as any)?.customer_type ?? "",
        points_available: totalBalance - totalPointsUsed,
        points_to_earn: totalPointsToEarn,
      } as ILoyaltyCustomerExtended & { points_available: number; points_to_earn: number };
      setLoyaltyCustomer(extendedCustomer);
    }
  }

  async function applyLoyaltyCashBackDiscount(
    reward: Array<{ reward_id: number; points_to_use: number; cart_line_item_id: number }>,
  ) {
    if (cart) {
      dispatch(enqueue());
      const removeReward = cart?.discount_lines?.find(discountLine =>
        reward?.some(r => r?.reward_id === discountLine?.loyalty_points_reward_id),
      );
      if (removeReward) {
        const res = await DeleteDiscountLine({ id: removeReward?.id, cart_id: cart?.id }, false);
        if (res?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(errorMessage(res, t("Error removing rewards discount"))));
          return;
        }
      }
      let updatedCart: ICart = null;
      const res = await PostLoyaltyProgramDiscount({ cart_id: cart?.id, reward_line_items: reward }, false);
      dispatch(dequeue());
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(errorMessage(res, t("Error adding rewards discounts"))));
        return;
      }
      updatedCart = res?.data;
      //Fallback if updated cart is not returned
      if (!updatedCart) {
        const cartRes = await GetCart({ id: cart?.id }, false);
        if (cartRes?.status !== StatusCode.OK) {
          dispatch(showError(t("Error updating cart")));
          return;
        }
        updatedCart = cartRes?.data[0];
      }
      //Set updated cart
      if (isStateCart(dataOrigin)) {
        updateState({ cart: updatedCart }, StateType.CheckInState);
      } else {
        void dispatch(loadTableCart({ tableCart: updatedCart }, false));
      }

      dispatch(showSuccess(t("Successfully added reward discount")));
      void closeRedeemPointsModal();
    }
  }

  async function removeCashbackDiscount(id: number) {
    let updatedCart: ICart = null;
    const res = await DeleteDiscountLine({ id, cart_id: cart?.id }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(res, t("Error removing rewards discount"))));
      return;
    }
    //Fallback if updated cart is not returned
    if (!updatedCart) {
      const cartRes = await GetCart({ id: cart?.id }, false);
      if (cartRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating cart")));
        return;
      }
      updatedCart = cartRes?.data[0];
    }
    //Set updated cart
    if (isStateCart(dataOrigin)) {
      updateState({ cart: updatedCart }, StateType.CheckInState);
    } else {
      void dispatch(loadTableCart({ tableCart: updatedCart }, false));
    }

    dispatch(showSuccess(t("Successfully removed reward discount")));
    void closeRedeemPointsModal();
  }

  async function applyLoyaltyProgramDiscount(rewardsParam: Array<{ reward_id: number; cart_line_item_id: number }>) {
    if (cart) {
      let updatedCart: ICart = null;

      //Get rewards that were added
      const addedRewards = rewardsParam?.filter(
        reward =>
          !cart?.discount_lines?.some(
            discount =>
              discount?.cart_line_item_id === reward?.cart_line_item_id &&
              discount?.loyalty_points_reward_id === reward?.reward_id,
          ),
      );
      //Get rewards that were removed
      const removedRewards = cart?.discount_lines?.filter(
        discountLine =>
          !rewardsParam?.some(
            reward =>
              reward?.cart_line_item_id === discountLine?.cart_line_item_id &&
              reward?.reward_id === discountLine?.loyalty_points_reward_id,
          ),
      );

      //No changes made, close the modal
      if (addedRewards?.length === 0 && removedRewards?.length === 0) {
        void closeRedeemPointsModal();
        return;
      }

      dispatch(enqueue());

      if (removedRewards?.length > 0) {
        //Remove all loyalty rewards discount lines
        const removeRes = await Promise.all(
          removedRewards?.map(
            async discountLine =>
              await DeleteDiscountLine({ id: discountLine?.id, cart_id: discountLine?.cart_id }, false),
          ),
        );
        if (removeRes?.some(remove => remove?.status !== StatusCode.OK)) {
          dispatch(dequeue());
          dispatch(showError(t("Error removing loyalty program discount")));
          return;
        }
        updatedCart = removeRes?.reverse()?.find(res => res?.status === StatusCode.OK)?.data;
      }

      if (addedRewards?.length > 0) {
        //Add the newly selected loyalty discounts
        const res = await PostLoyaltyProgramDiscount({ cart_id: cart?.id, reward_line_items: addedRewards }, false);
        if (res?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(t("Error adding rewards discounts")));
          return;
        }
        updatedCart = res?.data;
      }

      //Fallback if updated cart is not returned
      if (!updatedCart) {
        const cartRes = await GetCart({ id: cart?.id }, false);
        if (cartRes?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(t("Error updating cart")));
          return;
        }
        updatedCart = cartRes?.data[0];
      }

      dispatch(dequeue());

      //Set updated cart
      if (isStateCart(dataOrigin)) {
        updateState({ cart: updatedCart }, StateType.CheckInState);
      } else {
        void dispatch(loadTableCart({ tableCart: updatedCart }, false));
      }

      dispatch(showSuccess(t("Successfully added reward discounts")));
      void closeRedeemPointsModal();
    }
  }

  function processAmountSpentOffer(offer: IOffer) {
    let pointsEarned = 0;
    if (offer?.amount_spent <= 0) {
      return pointsEarned;
    }
    const totalSpent = validateLineItemsTotal();

    if (totalSpent >= offer?.amount_spent) {
      pointsEarned = Math.round(totalSpent) * offer?.points_earned;
    }

    return pointsEarned;
  }

  function validateLineItemsTotal() {
    let lineItemsTotal = 0;
    const excludedDepartmentIds = facilityStore?.loyaltyProgram?.excluded_departments?.map(
      department => department?.id,
    );
    if (cart?.line_items?.length > 0) {
      cart?.line_items?.forEach(lineItem => {
        const lineItemProduct = lineItem?.product;

        //Don't count discounts items if the loyalty program doesn't allow it
        if (facilityStore?.loyaltyProgram?.exclude_discounted_items) {
          const foundDiscount = cart?.discount_lines?.some(line => line?.cart_line_item_id === lineItem?.id);
          if (foundDiscount) {
            return;
          }
        }

        if (lineItem?.loyalty_points_override != null) {
          return;
        }

        //Check if line item's department is excluded from earning points
        if (
          (lineItemProduct?.department_id == null || !excludedDepartmentIds?.includes(lineItemProduct.department_id)) &&
          (lineItemProduct?.category_id == null || !excludedDepartmentIds?.includes(lineItemProduct.category_id)) &&
          (lineItemProduct?.subcategory_id == null || !excludedDepartmentIds?.includes(lineItemProduct.subcategory_id))
        ) {
          lineItemsTotal += lineItem?.subtotal_price;
        }
      });
    }

    return lineItemsTotal;
  }

  async function handleClearServiceFee() {
    const params = {
      // service_fee_id: selectedFee?.id,
      facility_id: facilityStore?.facility?.id,
      cart_id: cart?.id,
    };

    const cartRes = await DeleteServiceFeeLineItem(params, true);

    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error clearing service fee")));
      return;
    }

    //State cart (Tee Sheet Details)
    if (isStateCart(dataOrigin)) {
      updateState({ cart: cartRes?.data }, StateType.CheckInState);
    } else {
      //Redux cart (Register)
      void dispatch(loadTableCart({ tableCart: cartRes?.data }, false));
    }
  }

  function handleNoteChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDiscountModal(prevState => ({ ...prevState, noteDiscount: e.target.value }));
  }

  function getModifierLineItemIds(lineItem: ICartLineItem) {
    const cartLineItems = [...cart.line_items];
    const parentLineItemId = lineItem?.id;
    return cartLineItems?.filter(lineItem => lineItem?.parent_id === parentLineItemId)?.map(lineItem => lineItem?.id);
  }

  async function handleApplyAllLineItemsDiscount(customAmount?: string, typeFixed?: boolean) {
    if (cart && discountModal?.selectedDiscountId) {
      if (cart?.line_items?.length <= 0) {
        dispatch(showError(t("Cart does not contain any items")));
        return;
      }

      const type = typeFixed !== undefined ? (typeFixed ? "fixed_amount" : "percent") : undefined;
      const selectedDiscount = discountModal.selectedDiscount as IDiscount | null;
      const isFixedAmount = type === "fixed_amount" || (!type && selectedDiscount?.value_type === "fixed_amount");
      const isPercent = type === "percent" || (!type && selectedDiscount?.value_type === "percent");
      const discountId = discountModal.selectedDiscountId;
      const discountNotes = discountModal?.noteDiscount;
      const parentLineItems = cart.line_items.filter(lineItem => !lineItem?.parent_id);

      // Add loader for all line items
      parentLineItems.forEach(lineItem => {
        void addLineItemUpdating(lineItem?.id);
      });

      setDiscountModal({
        isOpen: false,
        selectedDiscountId: -1,
        selectedDiscount: null,
        noteDiscount: "",
      });

      // Fixed order discounts are split across line items by subtotal share so the
      // total discount equals the entered amount once (not once per line item).
      const fixedAmountsByLineItemId: Record<number, number> = {};
      if (isFixedAmount) {
        const totalFixedAmount = Number(
          customAmount !== undefined && customAmount !== "" ? customAmount : selectedDiscount?.value,
        );
        const lineSubtotals = parentLineItems.map(lineItem => Number(lineItem?.subtotal_price ?? lineItem?.price ?? 0));
        const cartSubtotal = lineSubtotals.reduce((sum, amount) => sum + amount, 0);

        if (!Number.isFinite(totalFixedAmount) || totalFixedAmount <= 0 || cartSubtotal <= 0) {
          parentLineItems.forEach(lineItem => {
            void removeLineItemUpdating(lineItem?.id);
          });
          dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.020")));
          return;
        }

        let allocated = 0;
        parentLineItems.forEach((lineItem, index) => {
          const isLast = index === parentLineItems.length - 1;
          const amount = isLast
            ? Number((totalFixedAmount - allocated).toFixed(2))
            : Number(((totalFixedAmount * lineSubtotals[index]) / cartSubtotal).toFixed(2));
          allocated = Number((allocated + amount).toFixed(2));
          fixedAmountsByLineItemId[lineItem.id] = amount;
        });
      }

      const discountResponses = await Promise.all(
        parentLineItems
          .filter(lineItem => !isFixedAmount || (fixedAmountsByLineItemId[lineItem.id] ?? 0) > 0)
          .map(async lineItem => {
            const modifierLineItemIds = getModifierLineItemIds(lineItem);
            const value = isFixedAmount
              ? fixedAmountsByLineItemId[lineItem.id]
              : customAmount
              ? Number(customAmount)
              : undefined;

            return await PostDiscountLine(
              {
                discount_id: discountId,
                cart_id: lineItem?.cart_id,
                cart_line_item_id: lineItem?.id,
                value,
                value_type: type,
                notes: discountNotes,
                modifier_line_item_ids: modifierLineItemIds?.length > 0 && isPercent ? modifierLineItemIds : undefined,
              },
              false,
            );
          }),
      );
      if (discountResponses?.some(res => res?.status !== StatusCode.OK)) {
        dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.020")));
      }

      const cartRes = await GetCart({ id: cart?.id }, false);
      if (cartRes?.status !== StatusCode.OK || cartRes?.data?.length === 0) {
        dispatch(showError(t("Error getting updated cart")));
        return;
      }

      const updatedCart = cartRes?.data[0];

      // Remove loader for all line items
      parentLineItems.forEach(lineItem => {
        void removeLineItemUpdating(lineItem?.id);
      });

      // State cart (Tee Sheet Details)
      if (isStateCart(dataOrigin)) {
        updateState({ cart: updatedCart }, StateType.CheckInState);
      } else {
        // Redux cart (Register)
        void dispatch(loadTableCart({ tableCart: updatedCart }, false));
      }

      dispatch(showSuccess(t("Discount applied successfully")));
    }
  }

  const [redeemTicketModal, setRedeemTicketModal] = useState<IRedeemTicketModalState>({
    isOpen: false,
    selectedTab: 0,
    ticketCode: "",
    availableTickets: [],
    selectedCustomerTickets: [],
    selectedCustomerTicketQuantities: {},
    scannedTickets: [],
    scannedTicketQuantities: {},
    ticketOffset: 0,
  });

  const handleOpenRedeemTicket = async () => {
    let availableTickets: any[] = [];
    if (cart?.customer?.id) {
      const ticketRes = await GetTicketStub(
        { customer_id: cart.customer.id, status: "valid", extended: true, limit: TICKET_LIMIT, offset: 0 },
        true,
      );
      if (ticketRes.status === StatusCode.OK) {
        availableTickets = ticketRes.data;
      }
    }
    setRedeemTicketModal(prev => ({
      ...prev,
      isOpen: true,
      availableTickets,
      selectedCustomerTickets: [],
      selectedCustomerTicketQuantities: {},
      scannedTickets: [],
      scannedTicketQuantities: {},
      ticketCode: "",
      selectedTab: 0,
      ticketOffset: 0,
    }));
  };

  const handleAvailableTicketsOffset = async (direction: "prev" | "next") => {
    if (!cart?.customer?.id) {
      return;
    }
    const newOffset =
      direction === "prev"
        ? redeemTicketModal.ticketOffset - TICKET_LIMIT
        : redeemTicketModal.ticketOffset + TICKET_LIMIT;
    if (newOffset < 0) {
      return;
    }
    const ticketRes = await GetTicketStub(
      { customer_id: cart.customer.id, status: "valid", extended: true, limit: TICKET_LIMIT, offset: newOffset },
      true,
    );
    if (ticketRes.status === StatusCode.OK) {
      setRedeemTicketModal(prev => ({
        ...prev,
        availableTickets: ticketRes.data,
        ticketOffset: newOffset,
      }));
    }
  };

  const handleScanTicket = async () => {
    const code = redeemTicketModal.ticketCode.trim();
    if (!code) {
      dispatch(showError(t("Please enter a ticket code")));
      return;
    }

    if (redeemTicketModal.scannedTickets.some((t: any) => t.code === code)) {
      dispatch(showError(t("Ticket already scanned")));
      return;
    }

    const validateRes = await GetTicketValidate({ code }, true);
    if (validateRes.status !== StatusCode.OK) {
      dispatch(showError(typeof validateRes.data === "string" ? validateRes.data : t("Invalid ticket code")));
      return;
    }
    const stub = validateRes.data?.stub || validateRes.data;
    if (stub) {
      setRedeemTicketModal(prev => ({
        ...prev,
        scannedTickets: [...prev.scannedTickets, stub],
        scannedTicketQuantities: isMultiUseTicket(stub)
          ? { ...prev.scannedTicketQuantities, [stub.id]: 1 }
          : prev.scannedTicketQuantities,
        ticketCode: "",
      }));
    }
  };

  const removeScannedTicket = (index: number) => {
    setRedeemTicketModal(prev => {
      const removedStub = prev.scannedTickets[index];
      const scannedTicketQuantities = { ...prev.scannedTicketQuantities };
      if (removedStub?.id) {
        delete scannedTicketQuantities[removedStub.id];
      }

      return {
        ...prev,
        scannedTickets: prev.scannedTickets.filter((_, i) => i !== index),
        scannedTicketQuantities,
      };
    });
  };

  const addRedeemedStubsToCartMultiple = async (redeemedStubs: IStub[], cartId?: number): Promise<boolean> => {
    if (!redeemedStubs || !redeemedStubs.length) {
      return true;
    }
    if (!cartId) {
      dispatch(showError(t("Failed to add redemption products to cart")));
      return false;
    }

    const variantsToAdd = buildRedemptionVariantsFromRedeemedStubs(redeemedStubs);
    const hasProducts = redeemedStubs.some(stub => stubHasRedemptionProducts(stub));

    if (variantsToAdd.length === 0) {
      if (hasProducts) {
        dispatch(showError(t("Failed to add redemption products to cart")));
        return false;
      }
      return true;
    }

    const updatedCartRes = await PostLineItemToCart({ cart_id: cartId, variants: variantsToAdd }, true);
    if (updatedCartRes?.status === StatusCode.OK && updatedCartRes?.data) {
      if (isStateCart(dataOrigin)) {
        updateState({ cart: updatedCartRes.data }, StateType.CheckInState);
      } else {
        void dispatch(loadTableCart({ tableCart: updatedCartRes.data }, false));
      }
      return true;
    }

    dispatch(showError(t("Failed to add redemption products to cart")));
    return false;
  };

  const handleRedeemTicket = async () => {
    const cartId = cart?.id;
    const customerId = cart?.customer?.id ?? undefined;

    if (redeemTicketModal.selectedTab === 0) {
      if (redeemTicketModal.scannedTickets.length === 0) {
        dispatch(showError(t("Please scan at least one ticket")));
        return;
      }
      const stubIds = expandTicketStubIds(
        redeemTicketModal.scannedTickets as IStub[],
        redeemTicketModal.scannedTicketQuantities,
      );
      const redeemRes = await PutTicketStubRedeemMultiple({ ticket_stub_ids: stubIds, customer_id: customerId }, true);
      if (redeemRes.status !== StatusCode.OK) {
        dispatch(showError(typeof redeemRes.data === "string" ? redeemRes.data : t("Error redeeming tickets")));
        return;
      }

      const cartAddOk = await addRedeemedStubsToCartMultiple(redeemRes.data as Array<IStub>, cartId);
      if (cartAddOk) {
        dispatch(showSuccess(t("Ticket redeemed successfully")));
      }
      setRedeemTicketModal(prev => ({
        ...prev,
        isOpen: false,
        scannedTickets: [],
        scannedTicketQuantities: {},
        ticketCode: "",
      }));
    } else {
      if (redeemTicketModal.selectedCustomerTickets.length === 0) {
        dispatch(showError(t("Please select at least one ticket")));
        return;
      }
      const stubIds = expandTicketStubIds(
        redeemTicketModal.selectedCustomerTickets as IStub[],
        redeemTicketModal.selectedCustomerTicketQuantities,
      );
      const redeemRes = await PutTicketStubRedeemMultiple({ ticket_stub_ids: stubIds, customer_id: customerId }, true);
      if (redeemRes.status !== StatusCode.OK) {
        dispatch(showError(typeof redeemRes.data === "string" ? redeemRes.data : t("Error redeeming tickets")));
        return;
      }

      const cartAddOk = await addRedeemedStubsToCartMultiple(redeemRes.data as Array<IStub>, cartId);
      if (cartAddOk) {
        dispatch(showSuccess(t("Ticket redeemed successfully")));
      }
      setRedeemTicketModal(prev => ({
        ...prev,
        isOpen: false,
        selectedCustomerTickets: [],
        selectedCustomerTicketQuantities: {},
      }));
    }
  };

  async function hanpleApplyTaxExemptionsToCart() {
    if (cart?.line_items?.length <= 0) {
      dispatch(showError(t("Cart does not contain any items")));
      return;
    }

    //Add loader for all line items
    cart?.line_items?.forEach(lineItem => {
      void addLineItemUpdating(lineItem?.id);
    });

    const taxExemptLineItems = cart?.line_items?.filter(
      lineItem => lineItem?.product?.tax_exempt && cart.customer?.tax_exempt,
    );
    if (!taxExemptLineItems?.length) {
      dispatch(showError(t("Cart does not contain any tax exempt items")));
    }

    if (taxExemptLineItems?.length > 0) {
      const exemptionResponses = await Promise.all(
        taxExemptLineItems?.map(async lineItem => {
          return await PutLineItemToCart(
            {
              ...lineItem,
              tax_exemption_applied: true,
            },
            false,
          );
        }),
      );
      if (exemptionResponses?.some(res => res?.status !== StatusCode.OK)) {
        dispatch(showError(t("Error applying tax exemptions")));
      }
    }

    const cartRes = await GetCart({ id: cart?.id }, false);
    if (cartRes?.status !== StatusCode.OK || cartRes?.data?.length === 0) {
      dispatch(showError(t("Error getting updated cart")));
      return;
    }

    const updatedCart = cartRes?.data[0];

    //Remove loader for all line items
    cart?.line_items?.forEach(lineItem => {
      void removeLineItemUpdating(lineItem?.id);
    });

    //State cart (Tee Sheet Details)
    if (isStateCart(dataOrigin)) {
      updateState({ cart: updatedCart }, StateType.CheckInState);
    } else {
      //Redux cart (Register)
      void dispatch(loadTableCart({ tableCart: updatedCart }, false));
    }

    if (taxExemptLineItems?.length > 0) {
      dispatch(showSuccess(t("Tax exemptions applied successfully")));
    }
  }

  async function handleRemoveTaxExemptionsFromCart() {
    if (cart?.line_items?.length <= 0) {
      dispatch(showError(t("Cart does not contain any items")));
      return;
    }

    //Add loader for all line items
    cart?.line_items?.forEach(lineItem => {
      void addLineItemUpdating(lineItem?.id);
    });

    const taxExemptLineItems = cart?.line_items?.filter(lineItem => lineItem?.tax_exemption_applied);
    if (!taxExemptLineItems?.length) {
      dispatch(showError(t("Cart does not contain any tax exempt items")));
    }

    if (taxExemptLineItems?.length > 0) {
      const exemptionResponses = await Promise.all(
        taxExemptLineItems?.map(async lineItem => {
          return await PutLineItemToCart(
            {
              ...lineItem,
              tax_exemption_applied: false,
            },
            false,
          );
        }),
      );
      if (exemptionResponses?.some(res => res?.status !== StatusCode.OK)) {
        dispatch(showError(t("Error removing tax exemptions")));
      }
    }

    const cartRes = await GetCart({ id: cart?.id }, false);
    if (cartRes?.status !== StatusCode.OK || cartRes?.data?.length === 0) {
      dispatch(showError(t("Error getting updated cart")));
      return;
    }

    const updatedCart = cartRes?.data[0];

    //Remove loader for all line items
    cart?.line_items?.forEach(lineItem => {
      void removeLineItemUpdating(lineItem?.id);
    });

    //State cart (Tee Sheet Details)
    if (isStateCart(dataOrigin)) {
      updateState({ cart: updatedCart }, StateType.CheckInState);
    } else {
      //Redux cart (Register)
      void dispatch(loadTableCart({ tableCart: updatedCart }, false));
    }

    if (taxExemptLineItems?.length > 0) {
      dispatch(showSuccess(t("Tax exemptions removed successfully")));
    }
  }

  console.log("state", state);
  const renderContext = () => {
    if (state.page === "methods") {
      return (
        <div className="flex flex-1 flex-col">
          {state?.order ? (
            <Button onClick={handleCancelOrderClick} type="text" className="p-0">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={["far", "long-arrow-left"]} />
                <span>{t("secure.facility.register.register_new.cart_menu.022")}</span>
              </div>
            </Button>
          ) : (
            <Button onClick={navigateBack} type="text" className="p-0">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={["far", "long-arrow-left"]} />
                <span>{t("secure.facility.register.register_new.cart_menu.023")}</span>
              </div>
            </Button>
          )}
          <div className="flex flex-1 flex-col h-full overflow-y-scroll no-scrollbar">
            <CartTotalsNew
              currency={cart?.currency}
              subTotal={cart?.subtotal_price}
              taxLines={cart?.tax_lines}
              totalTip={cart?.total_tip}
              total={cart?.total_price}
              totalServiceFee={cart?.service_fee_total}
              handleAddServiceFee={
                isStateCart(dataOrigin)
                  ? serviceFeeId => {
                      void handleAddServiceFee(serviceFeeId);
                    }
                  : undefined
              }
              order={state?.order}
              handleTipTeeSheet={
                isStateCart(dataOrigin)
                  ? (tip, allocations) => {
                      void handleAddTip(tip, allocations);
                    }
                  : undefined
              }
              pointsToEarn={loyaltyCustomer?.points_to_earn}
              handleClearServiceFee={handleClearServiceFee}
            />

            {deposits && deposits?.length > 0 && showDepositCallout ? (
              <>
                <br />
                <Callout
                  title={t("Apply deposit")}
                  content={t("Enter the deposit amount for the players and apply it to this order.")}
                  type="info"
                  actions={{
                    primary: {
                      label: t("Apply"),
                      onClick: () => {
                        const depositPaymentMethod = facilityStore?.paymentOptions?.find(
                          option => option?.payment_method === "deposit",
                        );
                        if (depositPaymentMethod) {
                          setState(prevState => ({
                            ...prevState,
                            paymentOption: depositPaymentMethod,
                          }));
                          void updateDepositsModal({ isOpen: true });
                        } else {
                          dispatch(showError(t("Deposit payment method not set up")));
                        }
                      },
                      buttonType: "secondary",
                    },
                    secondary: { label: t("Dismiss"), onClick: () => setShowDepositCallout(false) },
                  }}
                />
              </>
            ) : null}

            {state?.order?.transactions ? (
              <CartTransactions transactions={state?.order?.transactions} currency={state?.order?.currency} />
            ) : null}

            <Divider />
            {cart?.payment_method_saved_on_tab ? (
              <div className="ui-checkbox-group mb-3">
                <CreditCardTab
                  changeCard
                  isSelected={state?.paymentOption === null && paymentMethodState?.useSavedMethod}
                  card={paymentMethodState?.selectedTabPaymentMethod ?? cart?.payment_method_saved_on_tab}
                  setSelectedCard={handleSelectSavedCard}
                  onChangeCard={() => setPaymentMethodState(prev => ({ ...prev, isOpen: true }))}
                />
              </div>
            ) : null}

            {cart?.payment_method_saved_on_tab ? (
              <p className="text-md text-semibold mb-3">{t("Other payment methods")}</p>
            ) : null}

            <PaymentMethods
              onChange={handlePaymentOptionChange}
              allowDeselect
              paymentOptions={
                (deposits && deposits?.length > 0) || (permissions?.payment_option_deposit && cart?.customer_id)
                  ? facilityStore?.paymentOptions
                  : facilityStore?.paymentOptions?.filter(option => option?.type !== "deposit")
              }
              cardReaderAvailable={reader === null ? false : true}
              cart={cart}
              paymentAmount={state.paymentAmount}
              value={state.paymentOption}
            />
          </div>

          <div className="mt-auto">
            <div className="flex flex-row justify-end align-center mt-4">
              {facilityStore?.paymentOptions?.length === 0 ? null : (
                <Input
                  label={t("secure.facility.register.register_new.cart_menu.024")}
                  prefix={getCurrencySymbol(cartStore?.cart?.currency ?? "CAD")}
                  placeholder={String(state.paymentAmount)}
                  onChange={changePaymentAmount}
                  value={String(state.paymentAmount)}
                />
              )}
            </div>
            <Divider />

            <div className="mb-3">
              {state?.paymentInProgress ? (
                <div className="flex justify-center">
                  <div className="h-8 w-8">
                    <Spin className="admin-header-spinner" />
                  </div>
                </div>
              ) : (
                <Button
                  type="primary"
                  block
                  disabled={!state.paymentOption?.payment_method && !cart.payment_method_saved_on_tab}
                  onClick={handleProcessPaymentCheck}
                >
                  {state.paymentOption?.payment_method === "gift_card"
                    ? t("secure.facility.register.register_new.cart_menu.053")
                    : t("Process payment")}
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (state.page === "receipt") {
      return (
        <div className="flex flex-1 flex-col">
          <span className="mr-auto text-2xl text-semibold">
            {t("secure.facility.register.register_new.cart_menu.080")} {state?.order?.name}
          </span>

          <CartLineItemsNew
            currency={cart?.currency}
            lineItems={cart?.line_items}
            discountLines={cart?.discount_lines}
          />

          <div className="mt-auto">
            <div className="flex">
              <div className="flex ml-auto flex-col w-64">
                <CartTotalsNew
                  currency={state?.order?.currency}
                  subTotal={state?.order?.subtotal_price}
                  taxLines={state?.order?.tax_lines}
                  totalTip={state?.order?.total_tip}
                  total={state?.order?.total_price}
                  pointsToEarn={loyaltyCustomer?.points_to_earn}
                />

                <CartTransactions transactions={state?.order?.transactions} currency={state?.order?.currency} />
              </div>
            </div>
          </div>
          <Divider />

          {state?.order?.balance <= 0 ? (
            <>
              <div className="mb-3">
                <Button
                  type="primary"
                  block
                  icon={<FontAwesomeIcon icon={faLongArrowRight} />}
                  onClick={() => setState(prevState => ({ ...prevState, page: "cart", order: undefined }))}
                >
                  {t("secure.facility.register.register_new.cart_menu.025")}
                </Button>
              </div>
              <div>
                <Button
                  type="default"
                  block
                  onClick={() => {
                    void loadReceipt(state?.order?.id);
                  }}
                >
                  <span>{t("secure.facility.register.register_new.cart_menu.026")}</span>
                </Button>
              </div>
            </>
          ) : (
            <div className="mb-3">
              <Button
                type="primary"
                block
                disabled={!state.paymentOption?.payment_method}
                onClick={handleProcessPaymentCheck}
              >
                {state.paymentOption?.payment_method === "gift_card"
                  ? t("secure.facility.register.register_new.cart_menu.096")
                  : t("secure.facility.register.register_new.cart_menu.097")}
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (state.page === "cart") {
      return (
        // <div className="flex flex-1 flex-col">
        <>
          <div className="ui-register-line-items">
            {cart && (
              <>
                <div className="flex justify-between items-center pb-2 ">
                  <div className="flex items-center gap-4">
                    {cart.is_tab ? (
                      <div>
                        <FontAwesomeIcon
                          icon={faArrowLeft}
                          onClick={() => {
                            void handleSaveTab();
                          }}
                          className="cursor-pointer"
                        />
                      </div>
                    ) : null}

                    {cart.is_tab ? (
                      // <p className="ui-register-cart-new-cart-title">{cart?.description}

                      // </p>
                      <div>
                        <NewBadgeGroup
                          primaryContent={cart?.customer?.full_name}
                          secondaryContent={cart?.description ? cart?.description : ""}
                          // icon={cart?.status === "complete" ? faCircleDollar : null}
                          badgeGroupStyle="tertiary"
                          badgeGroupType="default"
                          badgeGroupSize="small"
                          onClick={() =>
                            setFilterState({
                              openNewTab: true,
                              newTabCustomer: cart?.customer,
                              newTabDescription: cart?.description ? cart?.description : "",
                              currentCart: cart,
                            })
                          }
                        />
                        {(loyaltyCustomer &&
                          facilityStore?.loyaltyProgram &&
                          facilityStore?.loyaltyProgram?.type === "points_based") ||
                        facilityStore?.loyaltyProgram?.type === "cash_back" ? (
                          <p className="ui-register-cart-new-points">
                            Points balance:{" "}
                            <span
                              className={classNames({
                                "ui-register-cart-new-points-strike":
                                  loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance,
                              })}
                            >
                              {loyaltyCustomer?.points_balance}
                            </span>
                            {loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance ? (
                              <span>
                                <FontAwesomeIcon icon={faArrowRight} /> {loyaltyCustomer?.points_available}
                              </span>
                            ) : null}
                          </p>
                        ) : null}

                        {loyaltyCustomer &&
                        facilityStore?.loyaltyProgram &&
                        facilityStore?.loyaltyProgram?.type === "tier_based" ? (
                          <p className="ui-register-cart-new-points">
                            {loyaltyCustomer?.selected_tier?.title
                              ? `${t("Tier")}: ${loyaltyCustomer?.selected_tier?.title}`
                              : t("No Tier")}{" "}
                            •{" "}
                            <span
                              className={classNames({
                                "ui-register-cart-new-points-strike":
                                  loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance,
                              })}
                            >
                              {loyaltyCustomer?.points_balance}
                            </span>
                            {loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance ? (
                              <span>
                                <FontAwesomeIcon icon={faArrowRight} /> {loyaltyCustomer?.points_available}
                              </span>
                            ) : null}{" "}
                            {t("pts")}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="ui-register-cart-new-cart-title">{t("Cart")}</p>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <NavigationDropdownNew
                      showPlainTextLabel
                      rightAlign
                      label={
                        <Button size="small" type="secondary">
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </Button>
                      }
                      sections={[
                        [
                          {
                            icon: "print",
                            label: t("Print Receipt"),
                            disabled: cartLoading,
                            onClick: () => {
                              void handlePrintReceiptButton();
                            },
                          },
                          {
                            icon: "cart-xmark",
                            label: t("Clear Cart"),
                            disabled: cartLoading,
                            onClick: () => setState(prevState => ({ ...prevState, clearCartConfirmVisible: true })),
                            hidden: cart?.is_tab,
                          },
                          {
                            icon: "cart-xmark",
                            label: t("Delete Tab"),
                            disabled: cartLoading,
                            onClick: () => handleShowCloseTab(),
                            hidden: !(cart?.is_tab && !!authStore?.user?.permissions?.tabs_close),
                          },
                          {
                            icon: "ticket",
                            label: t("Redeem Ticket"),
                            disabled: cartLoading,
                            onClick: () => void handleOpenRedeemTicket(),
                          },
                          {
                            icon: "merge",
                            label: t("Combine tab"),
                            disabled: cartLoading,
                            onClick: () =>
                              setFilterState({
                                openCombineTab: true,
                                combineSourceTab: cart,
                              }),
                            hidden: !(cart?.is_tab && (registerTabs?.length ?? 0) > 1 && !!setFilterState),
                          },
                          {
                            icon: "percent",
                            label: t("Apply Discount"),
                            disabled: cartLoading || !facilityStore?.discountOptions || cart?.line_items?.length === 0,
                            onClick: () => setDiscountModal(prev => ({ ...prev, isOpen: true })),
                          },
                          {
                            customIcon: <CustomIcon src="/public/images/badge-percent-circle-xmark.svg" size="small" />,
                            label: t("Clear All Discounts"),
                            hidden: cart?.discount_lines?.length === 0,
                            onClick: () => updateClearDiscountsPopup({ isOpen: true }),
                          },
                          {
                            icon: "percent",
                            label: isAutoDiscountsDisabled(cart) ? t("Enable Discounts") : t("Disable Discounts"),
                            disabled: cartLoading,
                            hidden: !showDisableDiscountsOption(cart),
                            onClick: () => {
                              void handleToggleDisableAutoDiscounts();
                            },
                          },
                          {
                            icon: "star",
                            label: t("Redeem Points"),
                            disabled: cartLoading || state?.editCartLoading || cart?.total_price === 0,
                            onClick: () => updateRedeemPointsModal({ isOpen: true }),
                            hidden: !loyaltyCustomer,
                          },
                          {
                            icon: "list-ul",
                            label: t("View cart activity"),
                            disabled: cartLoading,
                            onClick: () => setViewCartAudit(true),
                          },
                          {
                            icon: "receipt",
                            label: cart?.is_tab ? t("Save") : t("Save as tab"),
                            disabled: cartLoading,
                            onClick: cart?.is_tab ? () => handleSaveTab() : () => handleOpenCreateTab(cart),
                            hidden: !setFilterState,
                          },
                          {
                            icon: "notes",
                            label: t("Add note to order"),
                            disabled: cartLoading,
                            onClick: () =>
                              setState(prevState => ({
                                ...prevState,
                                showAddNoteModal: true,
                                addNote: cart?.notes ?? "",
                              })),
                            hidden: false,
                          },
                          {
                            icon: "file",
                            label: t("Apply Tax Exemptions"),
                            disabled: cartLoading,
                            onClick: hanpleApplyTaxExemptionsToCart,
                            hidden:
                              cart.line_items?.filter(item => item.product?.tax_exempt).length === 0 ||
                              !cart.customer?.tax_exempt,
                          },
                          {
                            icon: "file-ban",
                            label: t("Remove Tax Exemptions"),
                            disabled: cartLoading,
                            onClick: handleRemoveTaxExemptionsFromCart,
                            hidden:
                              cart.line_items?.filter(item => item.tax_exemption_applied).length === 0 ||
                              !cart.customer?.tax_exempt,
                          },
                          {
                            icon: "memo",
                            label: t("Review Customer Details"),
                            disabled: cartLoading,
                            onClick: () => setReviewPlayersModalVisable(true),
                            hidden:
                              isStateCart(dataOrigin) ||
                              !facilityStore?.facility?.display_review_players_modal ||
                              !cart?.customer,
                          },
                        ],
                      ]}
                    />
                  </div>
                </div>

                {state?.editCartLoading ? (
                  <div style={{ height: "32px" }}>
                    <Spin />
                  </div>
                ) : cart?.is_tab ? null : cart.customer ? (
                  <div>
                    <div className="ui-register-cart-new-parent-container">
                      <div className="ui-register-cart-new-image-container">
                        <FontAwesomeIcon icon={["far", "user"]} className="customer-image" />
                      </div>
                      <div className="ui-register-cart-new-customer-details">
                        <p className="ui-register-cart-new-customer-title">{cart?.customer?.full_name}</p>
                        {cart?.customer?.email ? (
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip
                                id="cart-customer-email-tooltip"
                                className="ui-register-cart-new-customer-email-tooltip"
                              >
                                {cart.customer.email}
                              </Tooltip>
                            }
                          >
                            <div>
                              <p className="ui-register-cart-new-customer-subtitle ui-register-cart-new-customer-email">
                                {cart.customer.email}
                              </p>
                            </div>
                          </OverlayTrigger>
                        ) : null}
                        {cart?.customer?.customer_type && (
                          <p className="ui-register-cart-new-customer-subtitle">{cart?.customer?.customer_type}</p>
                        )}
                        {(loyaltyCustomer &&
                          facilityStore?.loyaltyProgram &&
                          facilityStore?.loyaltyProgram?.type === "points_based") ||
                        facilityStore?.loyaltyProgram?.type === "cash_back" ? (
                          <p className="ui-register-cart-new-points">
                            Points balance:{" "}
                            <span
                              className={classNames({
                                "ui-register-cart-new-points-strike":
                                  loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance,
                              })}
                            >
                              {loyaltyCustomer?.points_balance}
                            </span>{" "}
                            {loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance ? (
                              <span>
                                <FontAwesomeIcon icon={faArrowRight} /> {loyaltyCustomer?.points_available}
                              </span>
                            ) : null}
                          </p>
                        ) : null}

                        {loyaltyCustomer &&
                        facilityStore?.loyaltyProgram &&
                        facilityStore?.loyaltyProgram?.type === "tier_based" ? (
                          <p className="ui-register-cart-new-points">
                            {loyaltyCustomer?.selected_tier?.title
                              ? `${t("Tier")}: ${loyaltyCustomer?.selected_tier?.title}`
                              : t("No Tier")}{" "}
                            •{" "}
                            <span
                              className={classNames({
                                "ui-register-cart-new-points-strike":
                                  loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance,
                              })}
                            >
                              {loyaltyCustomer?.points_balance}
                            </span>
                            {loyaltyCustomer?.points_available < loyaltyCustomer?.points_balance ? (
                              <span>
                                <FontAwesomeIcon icon={faArrowRight} /> {loyaltyCustomer?.points_available}
                              </span>
                            ) : null}{" "}
                            {t("pts")}
                          </p>
                        ) : null}
                      </div>
                      <div className="ml-auto ui-register-cart-new-customer-remove-container">
                        <Button
                          className="ui-register-cart-new-customer-remove"
                          onClick={() => {
                            void removeCustomerFromCart();
                          }}
                          size="medium"
                          type="close"
                        >
                          {t("secure.facility.register.register_new.cart_menu.084")}
                        </Button>
                      </div>
                    </div>
                    <hr className="mb-2"></hr>
                  </div>
                ) : !cart.customer ? (
                  <SelectCustomer //IMPORTANT
                    onCustomerChange={customer => {
                      void handleCustomerSelection(customer.id);
                    }}
                    permissionLevel={"facility"}
                    newCustomer
                  />
                ) : null}
              </>
            )}
          </div>

          {isStateCart(dataOrigin) && cart && (
            <Button
              size="medium"
              type="secondary"
              block
              onClick={() => setAddItemState(prevState => ({ ...prevState, modalVisible: true }))}
              disabled={!cart || !!state?.order}
            >
              {t("secure.facility.register.register_new.cart_menu.115")}
            </Button>
          )}

          {cart?.notes ? (
            <p className="ui-register-cart-new-customer-subtitle mt-2 mb-2">{"Order Notes: " + cart?.notes}</p>
          ) : null}

          {!cart ? (
            <div style={{ display: "flex", margin: "auto 0" }}>
              <div className="text-center m-auto">
                <div style={{ height: "32px" }}>
                  <Spin />
                </div>
                <div>{t("secure.facility.register.register_new.cart_menu.108")}</div>
              </div>
            </div>
          ) : null}

          <div className="overflow-y-auto">
            <CartLineItemsNew
              currency={cart?.currency}
              lineItems={cart?.line_items}
              handleLineItemClick={cart?.status === "complete" ? undefined : lineItem => handleLineItemClick(lineItem)}
              discountLines={cart?.discount_lines}
              handleDiscountClick={(discountLine, parentLineItemId) =>
                setCancelDiscountState(prevState => ({
                  ...prevState,
                  showCancelDiscount: true,
                  selectedDiscount: discountLine,
                  parentLineItemId: parentLineItemId ? parentLineItemId : null,
                }))
              }
            />
          </div>

          <div className="mt-auto">
            {deposits && deposits?.length > 0 ? (
              <>
                <br />
                <Callout
                  title={t("Deposit available for prepaid players")}
                  content={t("Proceed to checkout and apply deposits to your order.")}
                  type="info"
                />
              </>
            ) : null}
            <CartTotalsNew
              currency={cart?.currency}
              subTotal={cart?.subtotal_price}
              taxLines={cart?.tax_lines}
              totalTip={cart?.total_tip}
              totalServiceFee={cart?.service_fee_total}
              total={cart?.total_price}
              order={state?.order}
              cart={cart}
              handleDiscountClick={discountLine =>
                setCancelDiscountState(prevState => ({
                  ...prevState,
                  showCancelDiscount: true,
                  selectedDiscount: { ...discountLine, cart_line_item_id: discountLine?.id },
                  parentLineItemId: null,
                }))
              }
              handleTipTeeSheet={
                isStateCart(dataOrigin) ? (tip, allocations) => void handleAddTip(tip, allocations) : undefined
              }
              handleAddServiceFee={
                isStateCart(dataOrigin)
                  ? serviceFeeId => {
                      void handleAddServiceFee(serviceFeeId);
                    }
                  : undefined
              }
              pointsToEarn={loyaltyCustomer?.points_to_earn}
              handleClearServiceFee={handleClearServiceFee}
            />
            <Divider />
            <div
              // className="mb-3 flex gap-2 flex-wrap"

              className={classNames("flex gap-2 flex-wrap")}
            >
              <Fragment>
                <div className="w-100 flex flex-row gap-1">
                  <Button type="secondary" block disabled={cartLoading} onClick={handleSendToKitchen}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                    {t("Send")}
                  </Button>
                  <Button type="secondary" block disabled={cartLoading} onClick={handlePrintReceiptButton}>
                    {t("Print Receipt")}
                  </Button>
                </div>
                <Button
                  type="primary"
                  disabled={
                    !cart ||
                    cart?.line_items?.length === 0 ||
                    cart?.status === "complete" ||
                    cartLoading ||
                    state?.editCartLoading
                      ? true
                      : false
                  }
                  block
                  icon={<FontAwesomeIcon icon={faLongArrowRight} />}
                  onClick={checkProducts}
                >
                  <span className="mr-2">
                    {cart?.status === "complete"
                      ? t("secure.facility.register.register_new.cart_menu.075")
                      : t("Checkout")}
                  </span>
                </Button>
              </Fragment>
            </div>
          </div>
        </>
      );
    }
  };

  async function handleToggleDisableAutoDiscounts() {
    if (!cart?.id) {
      return;
    }

    const cartRes = await PutCart({ id: cart.id, disable_auto_discounts: nextDisableAutoDiscountsValue(cart) }, true);

    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating cart")));
      return;
    }

    const updatedCart = cartRes.data as ICart;

    if (isStateCart(dataOrigin)) {
      updateState({ cart: updatedCart }, StateType.CheckInState);
    } else {
      void dispatch(loadTableCart({ tableCart: updatedCart }, false));
    }

    dispatch(showSuccess(isAutoDiscountsDisabled(updatedCart) ? t("Discounts disabled") : t("Discounts enabled")));
  }

  async function handleAddNote() {
    //using the notes created by the user, add this to the notes on the order

    const cartRes = await PutCart({ id: cart?.id, notes: state.addNote }, true);

    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding note to order")));
      return;
    }

    //State cart (Tee Sheet Details)
    if (isStateCart(dataOrigin)) {
      updateState({ cart: cartRes?.data }, StateType.CheckInState);
    } else {
      //Redux cart (Register)
      void dispatch(loadTableCart({ tableCart: cartRes?.data }, false));
    }

    setState(prevState => ({ ...prevState, showAddNoteModal: false, addNote: "" }));
    dispatch(showSuccess(t("Note saved")));
  }

  return (
    <>
      <div className="ui-register-menu_content">
        <CartAuditTrailDrawer open={viewCartAudit} onClose={() => setViewCartAudit(false)} cart={cart} />
        {/* Pop up to clear a cart */}
        <Popup
          open={state.clearCartConfirmVisible}
          type="warning"
          title={t("Clear Cart")}
          description={t("secure.facility.register.register_new.cart_menu.032")}
          onOk={() => {
            if (isStateCart(dataOrigin)) {
              void clearStateCart();
            } else {
              void handleClearCart();
            }
            setState(prevState => ({
              ...prevState,
              clearCartConfirmVisible: false,
              gift_cards: null,
              selectedLeagues: null,
              selectedTournaments: null,
              order: undefined,
            }));
          }}
          okText={t("secure.facility.register.register_new.cart_menu.033")}
          cancelText={t("secure.facility.register.register_new.cart_menu.081")}
          onCancel={() => {
            setState(prevState => ({ ...prevState, clearCartConfirmVisible: false }));
          }}
          backDropCancel={true}
        />

        {/* Popup to delete tab  */}
        <Popup
          open={state.showCloseTab}
          type="warning"
          title={t("Delete Tab?")}
          description={t(
            "Closing a tab before checkout will delete all items and can't be undone. Would you like to continue?",
          )}
          // okDisabled={state.closeTabReason === ""}
          onOk={handleCloseTab}
          okText={t("Yes, delete tab")}
          cancelText={t("Cancel")}
          onCancel={() => {
            setState(prevState => ({ ...prevState, showCloseTab: false, closeTabReason: "" }));
          }}
          backDropCancel={true}
        />

        {/* Payment Handler Sheet - Handles successes/errors */}
        <>
          {stripePaymentState.processing ? (
            <Popup
              type="info"
              open={stripePaymentState.sheetVisible}
              title={t("secure.facility.register.register_new.cart_menu.034")}
              description={t("secure.facility.register.register_new.cart_menu.035")}
              closable
              onCancel={stripePaymentState.onCancel}
              onOk={stripePaymentState.onOk}
              cancelText={stripePaymentState.cancelText}
              okText={stripePaymentState.okText}
              backDropCancel={false}
            />
          ) : (
            <>
              {stripePaymentState.success ? (
                <Popup
                  type="success"
                  open={stripePaymentState.sheetVisible}
                  title={t("secure.facility.register.register_new.cart_menu.036")}
                  description={t("secure.facility.register.register_new.cart_menu.037")}
                  closable
                  onCancel={stripePaymentState.onCancel}
                  onOk={stripePaymentState.onOk}
                  cancelText={stripePaymentState.cancelText}
                  okText={stripePaymentState.okText}
                  backDropCancel={false}
                />
              ) : (
                <Popup
                  type="error"
                  open={stripePaymentState.sheetVisible}
                  title={t("secure.facility.register.register_new.cart_menu.038")}
                  description={
                    stripePaymentState.message +
                    t("secure.facility.register.register_new.cart_menu.039") +
                    stripePaymentState.convertedCode
                  }
                  closable
                  onCancel={stripePaymentState.onCancel}
                  onOk={stripePaymentState.onOk}
                  cancelText={stripePaymentState.cancelText}
                  okText={stripePaymentState.okText}
                  backDropCancel={false}
                />
              )}
            </>
          )}
        </>

        {/* Manual Credit Card Sheet*/}
        <Portal isMounted={manualStripeState.visible}>
          <Sheet
            size={"small"}
            closable
            title={t("secure.facility.register.register_new.cart_menu.040")}
            cancelText={t("secure.facility.register.register_new.cart_menu.081")}
            open={manualStripeState.visible}
            okText={t("secure.facility.register.register_new.cart_menu.041")}
            okDisabled={!elementsComplete}
            cancelDisabled={manualProcessing}
            backDropCancel={false}
            onOk={() => {
              if (!submitTrigger) {
                setSubmitTrigger(!submitTrigger);
              }
            }}
            onCancel={async () => {
              await cancelManualCreditPaymentIntent(
                manualStripeState?.transaction?.payment_intent_id as string,
                manualStripeState?.transaction?.id as number,
              );
              if (elements) {
                elements.getElement(CardNumberElement).clear();
                elements.getElement(CardExpiryElement).clear();
                elements.getElement(CardCvcElement).clear();
              }
              setManualStripeState(prevState => ({
                ...prevState,
                visible: false,
                transaction: {},
              }));
            }}
          >
            <CheckoutForm
              onChange={handleCardSectionChange}
              onSubmit={() => handleManualProcessing()}
              trigger={submitTrigger}
              transaction={manualStripeState.transaction}
              onError={(message: string) => {
                setSubmitTrigger(false);
                setManualProcessing(false);
                dispatch(showError(message));
              }}
              onSuccess={async () => {
                // Clean values
                dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.042")));
                setManualStripeState(prevState => ({
                  ...prevState,
                  visible: false,
                  transaction: {},
                  elementComplete: {
                    cardCvc: false,
                    cardExpiry: false,
                    cardNumber: false,
                  },
                }));
                setSubmitTrigger(false);
                if (isStateCart(dataOrigin)) {
                  updateState({ isOpen: true, transactionComplete: true }, StateType.OrderComplete);
                }
                dispatch(enqueue());
                await updateOrder(state.order?.id, true);
                dispatch(dequeue());
              }}
            />
          </Sheet>
        </Portal>
        {/** Seperate Payment for Stripe used for server payments - uses hook that should be updated to handle network based payments */}
        <StripeTerminalSheet />
        <StripeTerminalRefundSheet />

        {/** Sheet to handle line-item quantity */}
        {lineItemState.lineItem && (
          <UpdateLineItemModalTemp
            lineItemState={lineItemState}
            setLineItemState={setLineItemState}
            cart={cart}
            permissions={permissions}
            discounts={facilityStore?.discountOptions}
            dataOrigin={isStateCart(dataOrigin) ? "state" : "redux"}
            openModifiersSheet={lineItem => {
              if (isStateCart(dataOrigin)) {
                setAddItemState(prevState => ({ ...prevState, modalVisible: true }));
              }
              updateRegisterState({ modifierLineItem: lineItem });
            }}
            openDynamicComboSheet={lineItem => {
              if (isStateCart(dataOrigin)) {
                setAddItemState(prevState => ({ ...prevState, modalVisible: true }));
              }
              updateRegisterState({ dynamicComboLineItem: lineItem });
            }}
            modalActions={{
              onOk: cart => updateState({ cart }, StateType.CheckInState),
              updateTabCart: cart => handleCartUpdate(cart),
            }}
          />
        )}

        <GiftCardRedeem
          redeemGiftCardsActive={state.giftCardActive}
          cart={cart}
          onCancel={handleCancelAddGiftCard}
          onOk={info => {
            void handleProcessPayment(info);
          }}
        />

        <GiftCardSell
          gift_cards={state.gift_cards}
          sellGiftCardsActive={state.sellGiftCardsActive}
          onOk={() => {
            void handleAddSellGiftCards();
          }}
          onCancel={cancelGiftCardsAndLeagues}
          setGiftCards={gift_cards => setState(prev => ({ ...prev, gift_cards }))}
        />

        <AccountsModal
          loadAccounts={state.accountsVisible}
          onCancel={closeAccountsModal}
          onOk={handleAccountsOk}
          customerId={cart?.customer?.id}
          searchAccountPermission={permissions?.accounts_charge_all}
        />

        <CreditBookModal
          loadCreditBooks={state.creditBookVisible}
          onCancel={closeCreditBookModal}
          onOk={handleCreditBooksOk}
          customerId={cart?.customer?.id}
          // searchAccountPermission={permissions?.credit}
        />

        <Portal isMounted={state.invoiceVisible}>
          <InvoiceModal
            loadInvoices={state.invoiceVisible}
            onCancel={closeInvoiceModal}
            onOk={handleInvoiceOk}
            customerId={cart?.customer_id}
          />
        </Portal>

        <LeagueSelectionModal
          open={state.leagueFeesActive}
          leagueFeeProducts={state.leagueFeeProducts}
          onCancel={cancelGiftCardsAndLeagues}
          onOk={setSelectedLeagues}
        />

        <TournamentSelectionModal
          open={state.tournamentFeesActive}
          tournamentFeeProducts={state.tournamentFeeProducts}
          onCancel={cancelGiftCardsAndLeagues}
          onOk={setSelectedTournaments}
        />

        {/* Redeem Loyalty Points Based */}
        <RedeemPointsModal
          open={redeemPointsModal?.isOpen && facilityStore?.loyaltyProgram?.type === "points_based"}
          onCancel={closeRedeemPointsModal}
          customer={loyaltyCustomer}
          lineItems={cart?.line_items}
          discountLines={cart?.discount_lines}
          onOk={rewards => applyLoyaltyProgramDiscount(rewards)}
        />

        <RedeemTierPointsModal
          open={redeemPointsModal?.isOpen && facilityStore?.loyaltyProgram?.type === "tier_based"}
          onCancel={closeRedeemPointsModal}
          customer={loyaltyCustomer}
          lineItems={cart?.line_items}
          discountLines={cart?.discount_lines}
          onOk={rewards => applyLoyaltyProgramDiscount(rewards)}
        />

        {/* Cash back reward program */}
        <RedeemCashbackPointsModal
          open={redeemPointsModal?.isOpen && facilityStore?.loyaltyProgram?.type === "cash_back"}
          onCancel={closeRedeemPointsModal}
          customer={loyaltyCustomer}
          currency={cart?.currency}
          discountLines={cart?.discount_lines}
          onOk={reward => applyLoyaltyCashBackDiscount(reward)}
          subtotal={cart?.subtotal_price}
          handleRemove={removeCashbackDiscount}
        />

        <Sheet
          title={t("secure.facility.register.register_new.cart_menu.064")}
          open={state.roomChargeSheetVisible}
          size="small"
          closable
          onCancel={closeRoomChargeSheet}
          onOk={handleProcessPayment}
          okDisabled={isEmpty(state.selectedOccupant?.guest_name)}
          okText={t("secure.facility.register.register_new.cart_menu.065")}
        >
          <Input
            label={t("secure.facility.register.register_new.cart_menu.066")}
            helpText={t("secure.facility.register.register_new.cart_menu.067")}
            type="search"
            onChange={handleOccupantSearchQueryChange}
            value={state.roomOccupantSearchQuery}
          />
          {state.roomOccupantsSearchResults.map((occupant, index) => {
            return (
              <div key={index} className="flex flex-row justify-between p-3 border-b">
                <Checkbox
                  size="small"
                  checked={isEqual(occupant, state.selectedOccupant)}
                  onChange={() =>
                    setState(prev => ({
                      ...prev,
                      selectedOccupant: cloneDeep(occupant),
                    }))
                  }
                  label={occupant.guest_name}
                />
              </div>
            );
          })}
        </Sheet>

        {/* Add items modal used on Tee Sheet Details  */}
        <Portal isMounted={addItemState?.modalVisible}>
          <Sheet
            title={t("secure.facility.register.register_new.cart_menu.109")}
            open={addItemState?.modalVisible}
            size="cartMenu"
            closable
            overflow
            onCancel={() => setAddItemState(prevState => ({ ...prevState, modalVisible: false, productSearch: "" }))}
            cancelText={t("secure.facility.register.register_new.cart_menu.110")}
          >
            <div className="ui-register">
              <div className="ui-register-folders_container">
                <FolderMenu
                  options={addItemState?.folders}
                  optionTitle="title"
                  defaultSelected={addItemState?.folders?.findIndex(
                    folder => folder?.id === addItemState?.selectedFolder?.id,
                  )}
                  onClick={option => {
                    void handleChangeSelectedFolder(option as IFolder);
                  }}
                />
              </div>
              <div className="ui-register-new-products-container">
                <div className="ui-register-new-products-content">
                  <div className="ui-register-top-bar">
                    <div className="flex justify-between mb-4">
                      <div className="flex-grow">
                        <Input
                          id="checkInProductId"
                          value={addItemState?.productSearch ?? ""}
                          onChange={value =>
                            setAddItemState(prevState => ({ ...prevState, productSearch: value.target.value }))
                          }
                          type="search"
                          placeholder={t("secure.facility.register.register.006")}
                          labelHidden
                          autoFocus
                          trailingButtons={[
                            <Button key={1} type="secondary" onClick={refreshFolderStorage}>
                              <Icon icon={faRefresh} size="medium" />
                            </Button>,
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={"product-content"}>
                    <ProductsNew
                      {...renderProducts()}
                      disabled={!cart || cart?.status === "complete" || state.order ? true : false}
                      cart={cart}
                      selectedFolder={addItemState?.selectedFolder}
                      productsLoadingMessage={addItemState?.productsLoadingMessage}
                      updateStateCart={updateState}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Sheet>
        </Portal>

        {/* Tee sheet details deposits */}
        <BookingDepositsModal
          open={depositsModal?.isOpen}
          onCancel={closeDepositsModal}
          onOk={deposit => {
            void updateDepositsModal({ isOpen: false });
            void handleProcessPayment(null, deposit);
          }}
          deposits={deposits}
        />

        {/* Customer deposits */}
        <CustomerDepositsModal
          open={customerDepositsModal?.isOpen}
          onOk={(deposit, amountToApply) => {
            void updateCustomerDepositsModal({ isOpen: false });
            void handleProcessPayment(null, { ...deposit, amountToApply });
          }}
          onClose={closeCustomerDepositsModal}
          customerId={cart?.customer_id}
          cartAmount={String(state.paymentAmount)}
        />

        {outstandingBalancePopup}

        {facilityStore?.discountOptions && (
          <DiscountLineItemModal
            title={t("Apply Discount to Order")}
            open={discountModal.isOpen}
            onCancel={() =>
              setDiscountModal({ isOpen: false, selectedDiscount: null, selectedDiscountId: -1, noteDiscount: "" })
            }
            onOk={(customAmount, typeFixed) => handleApplyAllLineItemsDiscount(customAmount, typeFixed)}
            noteDiscount={discountModal.noteDiscount}
            changeNoteState={handleNoteChange}
            onChange={(id: number, discount: IDiscount) =>
              setDiscountModal({ ...discountModal, selectedDiscountId: id, selectedDiscount: discount })
            }
            okDisabled={!discountModal.selectedDiscountId || discountModal.selectedDiscountId < 0}
            discounts={facilityStore?.discountOptions}
            selectedDiscount={discountModal.selectedDiscount}
          />
        )}

        <Popup
          type="warning"
          open={state.showRefundBeforeCancel}
          title={t("Payments already processed")}
          description={
            getOrderNetCollected(state?.order?.transactions) > 0
              ? `This order has ${displayCurrency(
                  state?.order?.currency,
                  getOrderNetCollected(state?.order?.transactions),
                )} already collected. Refund payments returns money to the original payment methods (card, gift card, etc.), then voids the order. Hand cash/check back to the customer.`
              : "This order has payments already processed. Refund payments returns money to the original payment methods, then voids the order. Hand cash/check back to the customer."
          }
          closable
          loading={state.refundingPayments}
          okDisabled={state.refundingPayments}
          cancelDisabled={state.refundingPayments}
          onOk={() => handleRefundBeforeCancel()}
          onCancel={() => {
            if (!state.refundingPayments) {
              setState(prevState => ({ ...prevState, showRefundBeforeCancel: false }));
            }
          }}
          okText={t("Refund payments & void")}
          cancelText={t("Cancel")}
        >
          <div style={{ marginTop: "12px" }}>
            <Button
              type="error-outline"
              block
              disabled={state.refundingPayments}
              onClick={handleContinueCancelWithoutRefund}
            >
              {t("Cancel without refunding")}
            </Button>
          </div>
        </Popup>

        <Popup
          type="warning"
          open={state.showCancelOrder}
          title={t("secure.facility.register.register_new.cart_menu.068")}
          description={t("secure.facility.register.register_new.cart_menu.069")}
          closable
          onOk={cancelOrder}
          onCancel={() => setState(prevState => ({ ...prevState, showCancelOrder: false, void_note: "" }))}
          okText={t("secure.facility.register.register_new.cart_menu.063")}
        >
          {facilityStore.teeSheetSettings?.orders_void_with_note ? (
            <Input
              id="void_note"
              value={state.void_note || ""}
              onChange={e => setState(prevState => ({ ...prevState, void_note: e.target.value }))}
              placeholder={t("Reason...")}
            />
          ) : null}
        </Popup>

        <Popup
          type="info"
          open={state.changePopUpVisible}
          title={`${t("secure.facility.register.register_new.cart_menu.070")} ${state.changeDue?.toFixed(2)}`}
          description=""
          closable
          onOk={() => setState(prevState => ({ ...prevState, changePopUpVisible: false, changeDue: null }))}
          okText={t("secure.facility.register.register_new.cart_menu.071")}
          backDropCancel={false}
          stacked
        />

        <Popup
          type="info"
          open={clearDiscountsPopup?.isOpen}
          title={t("Clear All Discounts")}
          description={t("Are you sure you want to clear all discounts on this cart?")}
          closable
          onOk={removeAllDiscountLines}
          onCancel={closeClearDiscountsPopup}
          okText={t("Clear")}
          backDropCancel={false}
          stacked
        />

        <Popup
          type="warning"
          open={cancelDiscountState.showCancelDiscount}
          title={t("secure.facility.register.register_new.cart_menu.082")}
          description={t("secure.facility.register.register_new.cart_menu.083")}
          closable
          onOk={() => {
            void removeDiscountLine();
          }}
          onCancel={() =>
            setCancelDiscountState(prevState => ({
              ...prevState,
              showCancelDiscount: false,
              selectedDiscount: null,
              parentLineItemId: null,
            }))
          }
          okText={t("secure.facility.register.register_new.cart_menu.084")}
        />
        {/* Redeem Ticket Sheet */}
        <Sheet
          title={t("Redeem Ticket")}
          open={redeemTicketModal.isOpen}
          size="medium"
          closable
          onCancel={() =>
            setRedeemTicketModal(prev => ({
              ...prev,
              isOpen: false,
              ticketCode: "",
              scannedTickets: [],
              scannedTicketQuantities: {},
              selectedCustomerTickets: [],
              selectedCustomerTicketQuantities: {},
            }))
          }
          onOk={(): void => void handleRedeemTicket()}
          okText={t("Redeem")}
          cancelText={t("Cancel")}
        >
          <div className="redeem-ticket-sheet-body">
            <Tabs
              tabs={[
                { id: "code", content: t("Scan") },
                { id: "customer", content: t("Available"), disabled: !cart?.customer?.id },
              ]}
              selected={redeemTicketModal.selectedTab}
              onSelect={(selected: number) => setRedeemTicketModal(prev => ({ ...prev, selectedTab: selected }))}
            >
              {redeemTicketModal.selectedTab === 0 && (
                <>
                  <FormLayout>
                    <FormLayout.Group>
                      <Input
                        id="redeemTicketCode"
                        placeholder={t("Scan Ticket / Barcode")}
                        value={redeemTicketModal.ticketCode}
                        onChange={e => setRedeemTicketModal(prev => ({ ...prev, ticketCode: e.target.value }))}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            void handleScanTicket();
                          }
                        }}
                        trailingButtons={[
                          <Button key={1} onClick={() => void handleScanTicket()} type="secondary">
                            {t("Enter")}
                          </Button>,
                        ]}
                      />
                    </FormLayout.Group>
                  </FormLayout>

                  <DataTable
                    className="mt-3 redeem-ticket-table"
                    textWrap
                    columns={[
                      { label: t("Title") },
                      { label: t("Ticket #") },
                      { label: t("Barcode") },
                      { label: t("Quantity") },
                      { label: "" },
                      { label: "" },
                    ]}
                  >
                    {redeemTicketModal.scannedTickets.map((ticketStub: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <p className="text-xs">{ticketStub.ticket?.title}</p>
                        </td>
                        <td>
                          <p className="text-xs">{ticketStub.code}</p>
                        </td>
                        <td>
                          <p className="text-xs">{ticketStub.code}</p>
                        </td>
                        <td>
                          {isMultiUseTicket(ticketStub as IStub) ? (
                            <TicketRedemptionQuantityInput
                              id={`register-scanned-ticket-quantity-${ticketStub.id}`}
                              value={getTicketRedemptionQuantity(
                                ticketStub.id as number,
                                redeemTicketModal.scannedTicketQuantities,
                              )}
                              max={getMaxRedemptionQuantity(ticketStub as IStub)}
                              onChange={quantity =>
                                setRedeemTicketModal(prev => ({
                                  ...prev,
                                  scannedTicketQuantities: {
                                    ...prev.scannedTicketQuantities,
                                    [ticketStub.id]: quantity,
                                  },
                                }))
                              }
                            />
                          ) : (
                            1
                          )}
                        </td>
                        <td>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="ticket-validated-tooltip">{t("Ticket Validated")}</Tooltip>}
                          >
                            <FontAwesomeIcon className="inventory-counts-success" size="1x" icon={faCircleCheck} />
                          </OverlayTrigger>
                        </td>
                        <td className="text-right">
                          <FontAwesomeIcon
                            onClick={() => removeScannedTicket(index)}
                            icon={faTrashCanXmark}
                            size="1x"
                            className="cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </DataTable>
                </>
              )}
              {redeemTicketModal.selectedTab === 1 && (
                <DataTable
                  className="mt-3 redeem-ticket-table"
                  textWrap
                  columns={[
                    { label: t("Title") },
                    { label: t("Number") },
                    { label: t("Expires") },
                    { label: t("Remaining Redemptions") },
                    { label: t("Quantity") },
                    { label: "" },
                  ]}
                  footer={{
                    tableLimit: TICKET_LIMIT,
                    tableOffset: redeemTicketModal.ticketOffset,
                    disableNextOffset:
                      !redeemTicketModal.availableTickets ||
                      !(redeemTicketModal.availableTickets.length === TICKET_LIMIT),
                    handleTableOffset: direction => void handleAvailableTicketsOffset(direction),
                  }}
                >
                  {redeemTicketModal.availableTickets
                    ?.filter(
                      (ticketStub: any) =>
                        ticketStub.status !== "expired" &&
                        ticketStub.status !== "cancelled" &&
                        ticketStub.status !== "redeemed" &&
                        (ticketStub.expiry_date >= new Date().toISOString() || !ticketStub.expiry_date),
                    )
                    .map((ticketStub: any, index: number) => {
                      const isSelected = redeemTicketModal.selectedCustomerTickets.some(
                        (s: any) => s.id === ticketStub.id,
                      );

                      return (
                        <tr key={index}>
                          <td>
                            <p className="text-xs">{ticketStub.ticket?.title}</p>
                            {ticketStub.ticket?.subtitle && (
                              <p className="text-xs text-secondary-text">{ticketStub.ticket.subtitle}</p>
                            )}
                            {ticketStub?.is_shared ? (
                              <Badge type="primary" size="xsmall">
                                {t("Shared")}
                              </Badge>
                            ) : null}
                          </td>
                          <td>
                            <p className="text-xs">{ticketStub.code}</p>
                          </td>
                          <td>
                            <p className="text-xs">{ticketStub.expiry_date ? ticketStub.expiry : t("No Expiry")}</p>
                          </td>
                          <td>{ticketStub?.redemptions_remaining}</td>
                          <td>
                            {isSelected && isMultiUseTicket(ticketStub as IStub) ? (
                              <TicketRedemptionQuantityInput
                                id={`register-available-ticket-quantity-${ticketStub.id}`}
                                value={getTicketRedemptionQuantity(
                                  ticketStub.id as number,
                                  redeemTicketModal.selectedCustomerTicketQuantities,
                                )}
                                max={getMaxRedemptionQuantity(ticketStub as IStub)}
                                onChange={quantity =>
                                  setRedeemTicketModal(prev => ({
                                    ...prev,
                                    selectedCustomerTicketQuantities: {
                                      ...prev.selectedCustomerTicketQuantities,
                                      [ticketStub.id]: quantity,
                                    },
                                  }))
                                }
                              />
                            ) : isSelected ? (
                              1
                            ) : null}
                          </td>
                          <td>
                            <Checkbox
                              size="medium"
                              checked={isSelected}
                              onChange={e => {
                                if (e.target.checked) {
                                  setRedeemTicketModal(prev => ({
                                    ...prev,
                                    selectedCustomerTickets: [...prev.selectedCustomerTickets, ticketStub],
                                    selectedCustomerTicketQuantities: isMultiUseTicket(ticketStub as IStub)
                                      ? { ...prev.selectedCustomerTicketQuantities, [ticketStub.id]: 1 }
                                      : prev.selectedCustomerTicketQuantities,
                                  }));
                                } else {
                                  setRedeemTicketModal(prev => {
                                    const selectedCustomerTicketQuantities = {
                                      ...prev.selectedCustomerTicketQuantities,
                                    };
                                    delete selectedCustomerTicketQuantities[ticketStub.id];

                                    return {
                                      ...prev,
                                      selectedCustomerTickets: prev.selectedCustomerTickets.filter(
                                        (s: any) => s.id !== ticketStub.id,
                                      ),
                                      selectedCustomerTicketQuantities,
                                    };
                                  });
                                }
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                </DataTable>
              )}
            </Tabs>
          </div>
        </Sheet>

        {renderContext()}
      </div>

      <ReviewPlayerDetailsModal
        onClose={() => setReviewPlayersModalVisable(false)}
        open={reviewPlayersModalVisable}
        customers={memoizedCustomer}
      />

      <OrderCompletePopup
        open={state.orderCompletePopup}
        setOpen={(open: boolean) => {
          void closeOrderCompletePopup(open);
        }}
        order={state.order}
      />

      <AttachGroupMembershipPopup
        open={state.attachMembershipPopup}
        order={state.order}
        slots={((state.order?.membership_slots ?? []) as TOrderMembershipSlot[]).filter(
          slot => !slot.customer_id && !slot.customer_membership_id,
        )}
        cartCustomer={cart?.customer}
        onCancel={() =>
          setState(prevState => ({
            ...prevState,
            attachMembershipPopup: false,
            orderCompletePopup: isStateCart(dataOrigin) ? prevState.orderCompletePopup : true,
          }))
        }
        onAssigned={async slots => {
          setState(prevState => ({
            ...prevState,
            attachMembershipPopup: false,
            orderCompletePopup: true,
            order: { ...prevState.order, membership_slots: slots },
          }));
          await promptFutureBookingPricingAfterMembershipOrder(
            {
              customer_id: state.order?.customer_id ?? cart?.customer_id,
              line_items: state.order?.line_items ?? cart?.line_items,
              membership_slots: slots,
              previousCartCustomerTypeId: previousCartCustomerTypeIdRef.current,
            },
            openFutureBookingPricingModalIfNeeded,
          );
        }}
      />

      {futureBookingPricingModal}

      <Sheet
        open={state.showAddNoteModal}
        size="small"
        title={t("Add Note to Order")}
        onCancel={() => setState(prevState => ({ ...prevState, showAddNoteModal: false, addNote: "" }))}
        onOk={() => {
          void handleAddNote();
        }}
      >
        <div className="pt-3 pb-3">
          <TextField
            label={t("Note")}
            value={state.addNote}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setState(prevState => ({ ...prevState, addNote: e.target.value }))
            }
            rows={4}
          />
        </div>
      </Sheet>

      {sendToKitchenModal?.isOpen && (
        <TableServiceSendLineItemsModal
          open={sendToKitchenModal.isOpen}
          selectedIds={getDefaultKitchenLineItemIds()}
          activeTable={{ carts: [cart] } as ITable}
          onOk={lineItemIds => handleSendChit(lineItemIds)}
          onCancel={closeSendToKitchenModal}
        />
      )}

      <Portal isMounted={paymentMethodState.isOpen}>
        <Sheet
          title={t("Payment methods")}
          open={paymentMethodState.isOpen}
          size="small"
          onOk={() => {
            void handleSaveChangedTabCard();
          }}
          onCancel={() =>
            setPaymentMethodState(prev => ({
              ...prev,
              isOpen: false,
              selectedTabPaymentMethod: cart?.payment_method_saved_on_tab ?? null,
            }))
          }
        >
          <div>
            {paymentMethodState?.customerPaymentMethods.map(method => (
              <div className="ui-checkbox-group mt-3" key={method.id}>
                <CreditCardTab
                  card={method}
                  isSelected={method.id === paymentMethodState?.selectedTabPaymentMethod?.id}
                  setSelectedCard={() => setPaymentMethodState(prev => ({ ...prev, selectedTabPaymentMethod: method }))}
                />
              </div>
            ))}
          </div>
        </Sheet>
      </Portal>
    </>
  );
}
function isStateCart(dataOrigin: TDataOrigin): dataOrigin is TStateOrigin {
  const keys = Object.keys(dataOrigin);
  const isStateCart = keys.find(key => key === "stateCart");
  return isStateCart !== undefined;
}
